<template>
  <div class="job-timeline-view">
    <!-- Main Timeline Container -->
    <div class="timeline-container" ref="timelineContainer">
      <!-- Timeline Header -->
      <div class="timeline-header">
        <!-- Job Labels Column -->
        <div class="job-labels-header text-uppercase text--secondary">
          <v-menu
            v-model="sectorMenu"
            :close-on-content-click="false"
            offset-y
            left
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                v-bind="attrs"
                v-on="on"
                @click.stop
              >
                <v-icon>mdi-dots-horizontal</v-icon>
              </v-btn>
            </template>

            <v-card min-width="200">
              <v-list>
                <!-- Hide Empty Lanes -->
                <v-list-item @click="toggleHideEmptyLanesForAllPools">
                  <v-list-item-icon>
                    <v-icon :style="{ color: areAllPoolsHidingEmptyLanes ? 'var(--v-primary-base)' : '' }">
                      {{ areAllPoolsHidingEmptyLanes ? 'mdi-eye' : 'mdi-eye-off' }}
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title :style="{ color: areAllPoolsHidingEmptyLanes ? 'var(--v-primary-base)' : '' }">
                      {{ areAllPoolsHidingEmptyLanes ? $t('show_empty_lanes') : $t('hide_empty_lanes') }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <!-- Collapse/Expand Pools -->
                <v-list-item @click="toggleCollapseForAllPools">
                  <v-list-item-icon>
                    <v-icon :style="{ color: areAllPoolsCollapsed ? 'var(--v-primary-base)' : '' }">
                      {{ areAllPoolsCollapsed ? 'mdi-unfold-more-horizontal' : 'mdi-unfold-less-horizontal' }}
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title :style="{ color: areAllPoolsCollapsed ? 'var(--v-primary-base)' : '' }">
                      {{ areAllPoolsCollapsed ? $t('expand_all_pools') : $t('collapse_all_pools') }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-divider></v-divider>

                <!-- Lane Ordering Options -->
                <v-subheader>{{ $t('order_lanes') }}</v-subheader>
                <v-list-item
                  v-for="option in laneOrderOptions"
                  :key="option.value"
                  @click="orderLanesForAllPools(option.value)"
                >
                  <v-list-item-content>
                    <v-list-item-title>{{ $t(option.label) }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card>
          </v-menu>

          <span>{{ $t('departments') }}</span>
        </div>

        <!-- Time Columns Header -->
        <div class="time-header" ref="timeHeader">
          <!-- Day Row -->
          <div class="day-header-row">
            <div
              v-for="dayGroup in dayGroups"
              :key="`day-${dayGroup.date}`"
              class="day-header-cell"
              :style="{ width: (dayGroup.hourCount * columnWidth) + 'px' }"
              :class="{ 'non-working-day': !dayGroup.hasWorkingHours }"
            >
              {{ dayGroup.label }}
            </div>
          </div>

          <!-- Hour Row -->
          <div class="hour-header-row">
            <div
              v-for="(column, index) in timeColumns"
              :key="`hour-${index}`"
              class="hour-header-cell"
              :style="{ width: columnWidth + 'px' }"
              :class="{
                'current-time': column.isCurrent,
                'non-working': !column.isWorkingTime
              }"
            >
              {{ column.timeLabel }}
            </div>
          </div>

          <!-- Header Action Buttons -->
          <div class="header-actions">
            <div class="actions-left">
              <v-btn
                icon
                x-small
                @click="goToPreviousDateUnit"
              >
                <v-icon small>mdi-chevron-left</v-icon>
              </v-btn>
              <v-btn
                icon
                x-small
                @click="goToPreviousGroupDateUnit"
              >
                <v-icon small>mdi-chevron-double-left</v-icon>
              </v-btn>
            </div>

            <div class="actions-right">
              <v-btn
                icon
                x-small
                @click="goToNextGroupDateUnit"
              >
                <v-icon>mdi-chevron-double-right</v-icon>
              </v-btn>
              <v-btn
                icon
                x-small
                @click="goToNextDateUnit"
              >
                <v-icon small>mdi-chevron-right</v-icon>
              </v-btn>
            </div>
          </div>
        </div>
      </div>

      <!-- Timeline Body -->
      <div class="timeline-body" ref="timelineBody">

        <div
          v-for="pool in visiblePools"
          :key="'pool-' + pool.pool_id"
          class="pool-container"
          :class="{
            'pool-drag-target': targetPoolId === pool.pool_id,
            'pool-collapsed': collapsedPools[pool.pool_id]
          }"
          :style="{ '--pool-color': pool.color }"
        >

        <div class="lane-row">
          <div class="lane-label pa-2">
            <v-menu
              v-model="poolMenus[pool.pool_id]"
              :close-on-content-click="false"
              offset-y
              left
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click.stop
                >
                  <v-icon>mdi-dots-horizontal</v-icon>
                </v-btn>
              </template>

              <v-card min-width="200">
                <v-list>
                  <!-- Hide Empty Lanes -->
                  <v-list-item @click="toggleHideEmptyLanes(pool.pool_id)">
                    <v-list-item-icon>
                      <v-icon>{{ hideEmptyLanesPerPool[pool.pool_id] ? 'mdi-eye' : 'mdi-eye-off' }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>{{ hideEmptyLanesPerPool[pool.pool_id] ? $t('show_empty_lanes') : $t('hide_empty_lanes') }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-divider></v-divider>

                  <!-- Lane Ordering Options -->
                  <v-subheader>{{ $t('order_lanes') }}</v-subheader>
                  <v-list-item
                    v-for="option in laneOrderOptions"
                    :key="option.value"
                    @click="orderLanes(pool.pool_id, option.value)"
                  >
                    <v-list-item-content>
                      <v-list-item-title>{{ $t(option.label) }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>

            <span class="pool-title text-uppercase text--secondary">{{ pool.pool_title }}</span>

            <v-spacer></v-spacer>

            <v-btn
              @click="togglePoolCollapse(pool.pool_id)"
              icon
              small
            >
              <v-icon>{{ collapsedPools[pool.pool_id] ? 'mdi-chevron-right' : 'mdi-chevron-down' }}</v-icon>
            </v-btn>
          </div>

          <!-- Lane Timeline -->
          <div class="lane-timeline" ref="laneTimeline">
            <!-- Time Grid Background -->
            <div class="time-grid">
              <div
                v-for="(column, index) in timeColumns"
                :key="`grid-${index}`"
                class="time-grid-column"
                :style="{ width: columnWidth + 'px' }"
                :class="{
                  'current-time': column.isCurrent,
                  'non-working': !column.isWorkingTime,
                  'weekend-divider': isWeekendDivider(column, index),
                  'day-divider': isDayDivider(column, index) && !isWeekendDivider(column, index),
                  'time-gap-divider': isTimeGapDivider(column, index) && !isWeekendDivider(column, index) && !isDayDivider(column, index)
                }"
              ></div>
            </div>
          </div>
        </div>

        <!-- Drop zone at the top of the pool -->
        <div
          v-if="isManualOrdering(pool.pool_id) && draggingLane"
          class="drop-zone drop-zone-top"
          @drop="handlePoolDropZoneDrop($event, pool.pool_id, 'top')"
          @dragover.prevent
          @dragenter="handleDropZoneDragEnter($event, 'top')"
          @dragleave="handleDropZoneDragLeave"
        >
        </div>

        <!-- Lane Rows -->
        <div
          v-for="lane in getPoolLanes(pool.pool_id)"
          :key="'lane-' + lane.lane_id"
          class="lane-row"
          :class="{
            'lane-dragging': draggingLane && draggingLane.lane_id === lane.lane_id,
            'lane-drag-over': dragOverLaneId === lane.lane_id && draggingLane && draggingLane.lane_id !== lane.lane_id
          }"
          :style="{ height: getLaneHeight(lane.id) + 'px' }"
          :draggable="isManualOrdering(pool.pool_id)"
          @drop="handleLaneDrop($event, lane)"
          @dragover.prevent
          @dragenter="handleLaneDragEnter($event, lane)"
          @dragleave="handleLaneDragLeave($event)"
          @dragstart="handleLaneDragStart($event, lane, pool.pool_id)"
          @dragend="handleLaneDragEnd"
        >
          <!-- Lane Label -->
          <div class="lane-label">
            <div class="lane-info">
              <div class="lane-title" :style="{ color: pool.color }">
                <v-icon
                  v-if="isManualOrdering(pool.pool_id)"
                  small
                  class="drag-handle mr-2"
                  :style="{ color: pool.color }"
                >
                  mdi-drag-vertical
                </v-icon>
                {{ lane.lane_title }}
              </div>
            </div>
          </div>

          <!-- Lane Timeline -->
          <div class="lane-timeline" ref="laneTimeline">
            <!-- Time Grid Background -->
            <div class="time-grid">
              <div
                v-for="(column, index) in timeColumns"
                :key="`grid-${index}`"
                class="time-grid-column"
                :style="{ width: columnWidth + 'px' }"
                :class="{
                  'current-time': column.isCurrent,
                  'non-working': !column.isWorkingTime,
                  'weekend-divider': isWeekendDivider(column, index),
                  'day-divider': isDayDivider(column, index) && !isWeekendDivider(column, index),
                  'time-gap-divider': isTimeGapDivider(column, index) && !isWeekendDivider(column, index) && !isDayDivider(column, index)
                }"
              ></div>
            </div>

            <!-- Job Cards -->
            <div
              v-for="job in getLaneJobs(lane.id)"
              :key="'job-' + job.job_id"
              class="job-card"
              :class=" [{
                'dragging': draggingJob && draggingJob.job_id === job.job_id,
                'resizing': resizingJob && resizingJob.job_id === job.job_id,
                'dragging-to-other-lane': draggingJob && draggingJob.job_id === job.job_id && targetLaneId && targetLaneId !== job.lane_id,
                'subtask-card': !!job.parent_job_id,
                'job-focused': focusedJobGroup && isJobInFocusedGroup(job),
                'job-dimmed': focusedJobGroup && !isJobInFocusedGroup(job),
                'job-expanded': isJobExpanded(job.job_id)
              }, getJobPunctualityClass(job)]"
              :style="getJobStyle(job, lane.id)"
              @mousedown="startDrag(job, $event)"
              @mouseenter="!resizingJob && !draggingJob ? hoveredJobId = job.job_id : null"
              @mouseleave="!resizingJob && !draggingJob ? hoveredJobId = null : null"
              @dragstart.prevent
            >
              <!-- Left Resize Handle -->
              <div
                v-if="canInteractWithJob(job)"
                class="resize-handle resize-left"
                :class="getJobPunctualityClass(job)"
                @mousedown.stop="startResize(job, 'left', $event)"
              ></div>

              <!-- Job Content -->
              <div v-if="job.view_only" class="job-content d-flex flex-column">
                {{ $t('occupied') }}
              </div>

              <div v-else class="job-content d-flex flex-column text-no-wrap pa-1">
                <span class="d-flex align-center mb-1">
                  <v-icon size="18">{{ getJobDefinitionById(job.job_definition_id)?.icon }}</v-icon>
                  <span class="job-title">{{ jobTitle(job) }}</span>
                </span>
                <span v-if="timelineViewMode !== 'days' || hoveredJobId === job.job_id || (draggingJob && draggingJob.job_id === job.job_id)" class="job-info mb-2 text-caption">{{ getUserName(job.users_id) }}</span>
                <span v-if="timelineViewMode !== 'days' || hoveredJobId === job.job_id || (draggingJob && draggingJob.job_id === job.job_id)" class="job-info d-flex align-center">
                  <v-chip
                    v-if="job.readonly"
                    class="mr-1"
                    color="success"
                    x-small
                  >{{ $t('finished') }}</v-chip>

                  <v-icon small>mdi-list-status</v-icon>
                  {{ getStatusById(job.status_id).status_title }}

                  <v-icon v-if="job.deadline" small class="ml-2">mdi-calendar-remove-outline</v-icon>
                  {{ getFormattedDeadline(job) }}

                  <v-icon small class="ml-2">mdi-checkbox-multiple-blank-outline</v-icon>
                  {{ getDeliverablesCount(job.deliverables) }}
                </span>
              </div>

              <!-- Job Actions -->
              <div v-if="!job?.view_only" class="job-actions">
                <v-menu
                  @input="handleActionMenuToggle(job.job_id, $event)"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      icon
                    >
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>

                  <v-list dense>
                    <v-list-item
                      v-for="(item, index) in jobCardActions(job)"
                      :key="index"
                      @click="item.action(job)"
                    >
                      <v-list-item-icon>
                        <v-icon>{{ item.icon }}</v-icon>
                      </v-list-item-icon>

                      <v-list-item-title>{{ item.label }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>

              <!-- Right Resize Handle -->
              <div
                v-if="canInteractWithJob(job)"
                class="resize-handle resize-right"
                :class="getJobPunctualityClass(job)"
                @mousedown.stop="startResize(job, 'right', $event)"
              ></div>
            </div>
          </div>
        </div>

        <div class="lane-timeline"></div>
      </div>

        <!-- Current Time Indicator -->
        <div
          v-if="currentTimePosition >= 0"
          class="current-time-indicator"
          :style="{ left: currentTimePosition + 'px' }"
        >
          <div class="time-line"></div>
          <div class="time-marker"></div>
        </div>
      </div>

      <!-- Loading Overlay -->
      <div v-if="loading" class="loading-overlay">
        <div class="loading-content">
          <v-progress-circular
            indeterminate
            color="primary"
            size="48"
          ></v-progress-circular>
          <div class="loading-text">{{ $t('loading') }}</div>
        </div>
      </div>
    </div>

    <!-- Lane Move Confirmation Dialog (shared component) -->
    <MoveToLaneDialog
      v-if="pendingMoveData"
      v-model="showMoveConfirmDialog"
      :pools="allowedPools"
      :statuses="statuses"
      :lanes="lanes"
      :user-lanes="userLanes"
      :groups="groups"
      :initial-selection="{
        jobId: pendingMoveData.jobId,
        poolId: pendingMoveData.selectedPoolId || pendingMoveData.toLane?.pool?.pool_id,
        statusId: pendingMoveData.newStatusId,
        laneId: pendingMoveData.newLaneId,
        slaHours: pendingMoveData.slaHours,
        sumSLA: pendingMoveData.sumSLA,
        jobDefinitionId: pendingMoveData.jobDefinitionId,
        originalStatusId: pendingMoveData.originalStatusId,
        originalLaneId: pendingMoveData.originalLaneId,
      }"
      @confirm="handleMoveDialogConfirm"
      @closed="cancelLaneMove"
      style="z-index: 10002;"
    />

    <!-- Create Subtask Dialog -->
    <MoveToLaneDialog
      v-model="showCreateSubtaskDialog"
      :pools="allowedPools"
      :statuses="statuses"
      :lanes="lanes"
      :user-lanes="userLanes"
      :groups="groups"
      :job="createSubtaskJobContext"
      mode="create_subtask"
      @confirm="handleCreateSubtaskConfirm"
      style="z-index: 10002;"
    />

    <!-- Finish Subtask Confirmation Dialog -->
    <v-dialog v-model="showFinishSubtaskDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="headline">
          {{ $t('finish_subtask') }}
        </v-card-title>

        <v-card-text v-if="finishSubtaskJobContext"
          v-html="$t('confirm_finish_subtask_message', { name: finishSubtaskJobContext.job_title })"
        >
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey darken-1"
            text
            @click="cancelFinishSubtask"
          >
            {{ $t('cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            @click="confirmFinishSubtask"
            text
          >
            {{ $t('confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Finish Job Confirmation Dialog -->
    <v-dialog v-model="showFinishJobDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="headline">
          {{ $t('finish_job') }}
        </v-card-title>

        <v-card-text v-if="finishJobJobContext"
          v-html="$t('confirm_finish_job_message', { name: finishJobJobContext.job_title })"
        >
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey darken-1"
            text
            @click="cancelFinishJob"
          >
            {{ $t('cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            @click="confirmFinishJob"
            text
          >
            {{ $t('confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Overlap Confirmation Dialog -->
    <v-dialog v-model="showOverlapConfirmDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="headline">
          {{ $t('confirm_reschedule') }}
        </v-card-title>

        <v-card-text v-if="pendingOverlapData">
          {{ $t('confirm_overlap_message') }}
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey darken-1"
            text
            @click="cancelOverlap"
          >
            {{ $t('cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            @click="confirmOverlap"
            text
          >
            {{ $t('reschedule') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Update Job Time in Lane Confirmation Dialog -->
    <v-dialog v-model="showUpdateJobTimeInLaneDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="headline">
          {{ $t('confirm_reschedule') }}
        </v-card-title>

        <v-card-text v-if="pendingJobToUpdateTimeInLane">
          <p v-if="pendingJobToUpdateTimeInLane.direction && pendingJobToUpdateTimeInLane.direction === 'left'">
             {{ $t('confirm_start_date_updated', {d: formatJobTimeLongDate(pendingJobToUpdateTimeInLane.startDate)}) }}
          </p>
          <p v-if="pendingJobToUpdateTimeInLane.direction && pendingJobToUpdateTimeInLane.direction === 'right'">
              {{ $t('confirm_end_date_updated', {d: formatJobTimeLongDate(pendingJobToUpdateTimeInLane.endDate)}) }}
          </p>
          <p v-if="!pendingJobToUpdateTimeInLane.direction">
             {{ $t('confirm_job_date_updated', {
                start_d: formatJobTimeLongDate(pendingJobToUpdateTimeInLane.startDate),
                end_d: formatJobTimeLongDate(pendingJobToUpdateTimeInLane.endDate),
              }) }}
          </p>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey darken-1"
            text
            @click="cancelUpdateJobTimeInLane"
          >
            {{ $t('cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            @click="confirmUpdateJobTimeInLane"
            text
          >
            {{ $t('reschedule') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { format, parseISO } from 'date-fns'
import { enUS, ptBR } from 'date-fns/locale'
import { mapState, mapActions, mapMutations } from 'vuex'

import NotificationActions from '@/store/core/notification/actions-types'
import JobActions from '@/store/job/actions-types'
import JobMutations from '@/store/job/mutations-types'
import CommentAPI from '@/services/api/job/comment'
import JobAPI from '@/services/api/job/job'
import MoveToLaneDialog from '@/components/job/MoveToLaneDialog.vue'

const LANE_PREFERENCES_STORAGE_VERSION = 1

export default {
  name: 'JobTimelineView',
  components: { MoveToLaneDialog },

  props: {
    groups: {
      type: Array,
      default: () => [],
    },
    users: {
      type: Array,
      default: () => [],
    },
    statuses: {
      type: Array,
      default: () => []
    },
    lanes: {
      type: Array,
      default: () => []
    },
    userLanes: {
      type: Array,
      default: () => []
    },
    pools: {
      type: Array,
      default: () => []
    },
    jobDefinitions: {
      type: Array,
      default: () => []
    },
    userAcls: {
      type: Array,
      default: () => []
    },
    appConfig: {
      type: Object,
      default: () => {},
    },
    timelineViewMode: {
      type: String,
      default: "hours"
    },
    columnsCount: {
      type: Number,
      default: 16
    },
    filterCampaign: {
      type: Array,
      default: () => []
    },
    filterCustomer: {
      type: Array,
      default: () => []
    },
    filterRequester: {
      type: Array,
      default: () => []
    },
    filterPool: {
      type: Array,
      default: () => []
    },
    filterLane: {
      type: Array,
      default: () => []
    },
    filterJobCustomerCode: {
      type: String,
      default: ''
    },
    filterStatus: {
      type: Array,
      default: () => []
    },
    filterPunctuality: {
      type: Array,
      default: () => []
    },
    filterJobs: {
      type: Array,
      default: () => []
    },
    filterShowFinished: {
      type: Boolean,
      default: false
    },
  },

  data() {
    return {
      loading: false,
      columnWidth: 60, // pixels per hour - will be calculated dynamically
      laneRowHeight: 90,
      currentTime: new Date(),
      timelineTime: new Date(),
      timeUpdateInterval: null,
      containerWidth: 0,

      workingSchedule: {
        "Monday": [
          { "start": "09:00", "end": "12:00" },
          { "start": "15:00", "end": "18:00" }
        ],
        "Tuesday": [
          { "start": "09:00", "end": "12:00" },
          { "start": "15:00", "end": "18:00" }
        ],
        "Wednesday": [
          { "start": "09:00", "end": "12:00" },
          { "start": "15:00", "end": "18:00" }
        ],
        "Thursday": [
          { "start": "09:00", "end": "12:00" },
          { "start": "15:00", "end": "18:00" }
        ],
        "Friday": [
          { "start": "09:00", "end": "12:00" },
          { "start": "15:00", "end": "18:00" }
        ],
        "Saturday": [],
        "Sunday": []
      },

      excludeWeekends: true,

      resizeDirection: null,
      resizingJob: null,
      draggingJob: null,
      dragStartX: 0,
      dragStartY: 0,
      dragStartTime: null,
      dragOffsetX: 0,
      dragOffsetY: 0,
      containerRect: null,
      targetLaneId: null,
      targetPoolId: null,
      showMoveConfirmDialog: false,
      pendingMoveData: null,
      showOverlapConfirmDialog: false,
      pendingOverlapData: null,
      showUpdateJobTimeInLaneDialog: false,
      pendingJobToUpdateTimeInLane: null,

      // Create subtask
      showCreateSubtaskDialog: false,
      createSubtaskJobContext: null,

      // Finish subtask
      showFinishSubtaskDialog: false,
      finishSubtaskJobContext: null,

      // Finish job
      showFinishJobDialog: false,
      finishJobJobContext: null,

      // Lane ordering
      sectorMenu: false,
      poolMenus: {},
      poolOrderTypes: {}, // Store ordering type for each pool
      collapsedPools: {}, // Store collapsed state for each pool
      hideEmptyLanesPerPool: {}, // Store hide empty lanes setting per pool
      laneOrderOptions: [
        {
          value: 'manual',
          label: 'order_manually',
        },
        {
          value: 'start_date',
          label: 'order_by_start_date',
        },
        {
          value: 'end_date',
          label: 'order_by_end_date',
        },
        {
          value: 'deadline',
          label: 'order_by_deadline',
        }
      ],

      // Lane drag and drop
      draggingLane: null,
      dragOverLaneId: null,
      dragOverDropZone: null, // 'top' or 'bottom'
      insertAfterTarget: false, // Whether to insert after the target lane
      laneManualOrders: {}, // Store manual ordering for each pool

      // Job hover state
      hoveredJobId: null,

      // Track which job's action menu is open
      openActionMenuJobId: null
    }
  },

  computed: {
    ...mapState({
      userData: state => state.core.auth.userData,
      jobs: state => state.job.jobs,
      focusedJobGroup: state => state.job.focusedJobGroup,
    }),

    // Get the current locale for date formatting
    currentLocale() {
      const locale = this.$i18n.locale
      // Map Vue i18n locales to date-fns locales
      const localeMap = {
        'en': enUS,
        'pt': ptBR,
        'pt-BR': ptBR
      }
      return localeMap[locale] || enUS
    },

    allowedPools() {
      const MOVE_ACL = 1
      const allowedIds = (this.userAcls || [])
        .filter(acl => acl.acl & MOVE_ACL)
        .map(acl => acl.pool_id)
      return (this.pools || []).filter(p => allowedIds.includes(p.pool_id))
    },

    columnOptions() {
      if (this.timelineViewMode === 'hours') {
        return [
          { text: '16 columns', value: 16 },
          { text: '24 columns', value: 24 },
          { text: '32 columns', value: 32 }
        ]
      } else {
        return [
          { text: '14 columns', value: 14 },
          { text: '21 columns', value: 21 },
          { text: '28 columns', value: 28 }
        ]
      }
    },

    timeColumns() {
      if (this.timelineViewMode === 'hours') {
        return this.generateHourColumns()
      } else {
        return this.generateDayColumns()
      }
    },

    currentTimePosition() {
      const headerWidth = 200 // Job labels column width
      const now = this.currentTime // Use real-world current time, not timeline navigation time

      if (this.timelineViewMode === 'hours') {
        return this.calculateHourTimePosition(now, headerWidth)
      } else {
        return this.calculateDayTimePosition(now, headerWidth)
      }
    },

    dayGroups() {
      if (this.timelineViewMode === 'days') {
        // For days view, group by weeks
        const groups = []
        let currentWeek = null
        let currentGroup = null

        this.timeColumns.forEach(column => {
          // Get the start of the week (Monday)
          const date = new Date(column.time)
          const dayOfWeek = date.getDay()
          const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek // Sunday = 0, Monday = 1
          const weekStart = new Date(date)
          weekStart.setDate(date.getDate() + mondayOffset)
          weekStart.setHours(0, 0, 0, 0)

          const weekKey = format(weekStart, 'yyyy-MM-dd')

          if (weekKey !== currentWeek) {
            if (currentGroup) {
              groups.push(currentGroup)
            }

            // Calculate week end (Sunday)
            const weekEnd = new Date(weekStart)
            weekEnd.setDate(weekStart.getDate() + 6)

            // Format week label with locale
            const startDay = format(weekStart, 'dd', { locale: this.currentLocale })
            const endDay = format(weekEnd, 'dd', { locale: this.currentLocale })
            const startMonth = format(weekStart, 'MMM', { locale: this.currentLocale })
            const endMonth = format(weekEnd, 'MMM', { locale: this.currentLocale })
            const year = format(weekStart, 'yyyy', { locale: this.currentLocale })

            let weekLabel
            if (startMonth === endMonth) {
              weekLabel = `${startDay} - ${endDay} ${startMonth} ${year}`
            } else {
              weekLabel = `${startDay} ${startMonth} - ${endDay} ${endMonth} ${year}`
            }

            currentGroup = {
              date: weekKey,
              label: weekLabel,
              hourCount: 1,
              hasWorkingHours: column.isWorkingTime
            }
            currentWeek = weekKey
          } else {
            currentGroup.hourCount++
            if (column.isWorkingTime) {
              currentGroup.hasWorkingHours = true
            }
          }
        })

        if (currentGroup) {
          groups.push(currentGroup)
        }

        return groups
      } else {
        // For hours view, group by days
        const groups = []
        let currentDay = null
        let currentGroup = null

        this.timeColumns.forEach(column => {
          const dayKey = format(column.time, 'yyyy-MM-dd')
          const dayLabel = format(column.time, 'MMM dd (EEE)', { locale: this.currentLocale })

          if (dayKey !== currentDay) {
            if (currentGroup) {
              groups.push(currentGroup)
            }

            currentGroup = {
              date: dayKey,
              label: dayLabel,
              hourCount: 1,
              hasWorkingHours: column.isWorkingTime
            }
            currentDay = dayKey
          } else {
            currentGroup.hourCount++
            if (column.isWorkingTime) {
              currentGroup.hasWorkingHours = true
            }
          }
        })

        if (currentGroup) {
          groups.push(currentGroup)
        }

        return groups
      }
    },

    filteredJobs() {
      let jobs = this.jobs

      if (this.filterCampaign.length > 0) {
        jobs = jobs.filter(j => this.filterCampaign.includes(this.getJobField(j, 'campaign_id')) )
      }

      if (this.filterCustomer.length > 0) {
        const customerFilter = this.filterCustomer.map(customerId => String(customerId))

        jobs = jobs.filter(j => customerFilter.includes(String(this.getCustomerId(j))) )
      }

      if (this.filterRequester.length > 0) {
        const requesterFilter = this.filterRequester.map(requesterId => String(requesterId))

        jobs = jobs.filter(j => requesterFilter.includes(String(this.getRequesterId(j))) )
      }

      if (this.filterPool.length > 0) {
        const poolFilter = this.filterPool.map(poolId => String(poolId))

        jobs = jobs.filter(j => poolFilter.includes(String(this.getPoolId(j))) )
      }

      if (this.filterLane.length > 0) {
        const laneFilter = this.filterLane.map(laneId => String(laneId))

        jobs = jobs.filter(j => laneFilter.includes(String(this.getLaneId(j))) )
      }

      if (this.filterJobCustomerCode) {
        const jobCustomerCodeFilter = this.filterJobCustomerCode.toLowerCase()

        jobs = jobs.filter(j => this.getJobCustomerCode(j).toLowerCase().includes(jobCustomerCodeFilter) )
      }

      if (this.filterStatus.length > 0) {
        jobs = jobs.filter(j => this.filterStatus.includes(j.status_id) )
      }

      if (this.filterPunctuality.length > 0) {
        jobs = jobs.filter(j => this.filterPunctuality.includes(this.getJobPunctualityClass(j)))
      }

      if (this.filterJobs.length > 0) {
        jobs = jobs.filter(j => this.filterJobs.includes(j.job_id))
      }

      return jobs
    },

    visiblePools() {
      return this.pools.filter(pool => this.shouldShowPool(pool.pool_id))
    },

    areAllPoolsHidingEmptyLanes() {
      return this.pools.length > 0
        && this.pools.every(pool => !!this.hideEmptyLanesPerPool[pool.pool_id])
    },

    areAllPoolsCollapsed() {
      return this.pools.length > 0
        && this.pools.every(pool => !!this.collapsedPools[pool.pool_id])
    },

    getPendingMovePoolLanes() {
      if (! this.pendingMoveData) {
        return []
      }

      return this.getPoolLanes(this.pendingMoveData.toLane.pool.pool_id).map((l) => ({
        title: l.lane_title,
        lane_id: l.lane_id
      }))
    },

    getPendingMovePoolStatuses() {
      if (! this.pendingMoveData) {
        return []
      }

      const poolStatusesIds = this.pendingMoveData.toLane.pool.statuses.map(st => st.status_id)

      return this.statuses.filter(s => poolStatusesIds.includes(s.status_id))
    },

    lanePreferencesStorageKey() {
      const userId = this.userData?.users_id || this.userData?.user_id || 'anonymous'
      const appId = this.$route.params.appId || this.appConfig?.application_instance_id || 'default'

      return `jobTimelineLanePreferences:v${LANE_PREFERENCES_STORAGE_VERSION}:user:${userId}:app:${appId}`
    }
  },

  mounted() {
    this.restoreLanePreferences()
    this.setSchedule()
    this.startTimeUpdater()
    this.setupGlobalEventListeners()
    this.calculateColumnWidth()

    // Add resize listener to recalculate column width
    window.addEventListener('resize', this.calculateColumnWidth)
  },

  beforeDestroy() {
    if (this.timeUpdateInterval) {
      clearInterval(this.timeUpdateInterval)
    }
    this.removeGlobalEventListeners()
    window.removeEventListener('resize', this.calculateColumnWidth)
  },

  watch: {
    columnsCount() {
      this.$nextTick(() => {
        this.calculateColumnWidth()
      })
    },

    timelineViewMode() {
      this.$nextTick(() => {
        this.calculateColumnWidth()
      })
    },

    filterShowFinished() {
      this.fetchJobs(this.timeColumns)
    },

    timeColumns(value) {
      this.fetchJobs(value)
    },

    lanes() {
      this.restoreLanePreferences()
    },

    lanePreferencesStorageKey() {
      this.restoreLanePreferences()
    },
  },

  methods: {
    ...mapActions('job', [
      JobActions.GET_ALL_JOBS,
      JobActions.MOVE_TO_POOL,
      JobActions.CHANGE_SLOT,
      JobActions.CREATE_SUBTASK,
      JobActions.FINISH_SUBTASK,
      JobActions.FINISH_JOB,
      JobActions.REOPEN_JOB,
    ]),

    ...mapActions('core/notification', [
      NotificationActions.SEND_MESSAGE,
    ]),

    ...mapMutations('job', [
      JobMutations.UPDATE_JOB,
      JobMutations.REMOVE_SUBTASKS_BY_PARENT_JOB_ID,
      JobMutations.REMOVE_JOB,
      JobMutations.SET_FOCUSED_JOB_GROUP,
    ]),

    setNewTimelineTime(newDate) {
      this.timelineTime = newDate
    },

    getJobField(job, fieldName) {
      return job?.[fieldName] ?? job?.fields?.[fieldName] ?? null
    },

    removeSubtasksForTerminalParent(job, statusId) {
      if (job?.parent_job_id) return

      const status = this.getStatusById(statusId)
      if (!['done', 'canceled'].includes(status?.status_type)) return

      this[JobMutations.REMOVE_SUBTASKS_BY_PARENT_JOB_ID](job.job_id)
    },

    getCustomerId(job) {
      return this.getJobField(job, 'customer_id') ?? job?.customer ?? job?.fields?.cooperada ?? null
    },

    getRequesterId(job) {
      return this.getJobField(job, 'requester_users_id')
        ?? this.getJobField(job, 'requesterUsersId')
        ?? this.getJobField(job, 'users_id')
    },

    getLaneId(job) {
      return this.getJobField(job, 'lane_id') ?? this.getJobField(job, 'laneId')
    },

    getPoolId(job) {
      const poolId = this.getJobField(job, 'pool_id') ?? this.getJobField(job, 'poolId')

      if (poolId) {
        return poolId
      }

      const laneId = this.getLaneId(job)
      const lane = this.lanes.find(lane => String(lane.lane_id) === String(laneId))

      return lane?.pool?.pool_id ?? lane?.pool_id ?? null
    },

    getJobCustomerCode(job) {
      return String(this.getJobField(job, 'job_customer_code') ?? '')
    },

    loadLanePreferences() {
      try {
        const storedPreferences = localStorage.getItem(this.lanePreferencesStorageKey)

        if (!storedPreferences) {
          return {}
        }

        const parsedPreferences = JSON.parse(storedPreferences)

        if (parsedPreferences?.version !== LANE_PREFERENCES_STORAGE_VERSION) {
          return {}
        }

        return parsedPreferences.pools || {}
      } catch (error) {
        console.warn('Error loading lane preferences:', error)
        return {}
      }
    },

    saveLanePreferences() {
      try {
        const poolsPreferences = {}

        this.pools.forEach((pool) => {
          const poolId = pool.pool_id

          poolsPreferences[poolId] = {
            collapsed: !!this.collapsedPools[poolId],
            hideEmptyLanes: !!this.hideEmptyLanesPerPool[poolId],
            orderType: this.poolOrderTypes[poolId] || null,
            manualOrder: Array.isArray(this.laneManualOrders[poolId])
              ? this.laneManualOrders[poolId]
              : [],
          }
        })

        localStorage.setItem(this.lanePreferencesStorageKey, JSON.stringify({
          version: LANE_PREFERENCES_STORAGE_VERSION,
          pools: poolsPreferences,
        }))
      } catch (error) {
        console.warn('Error saving lane preferences:', error)
      }
    },

    restoreLanePreferences() {
      const preferences = this.loadLanePreferences()
      const validOrderTypes = this.laneOrderOptions.map(option => option.value)

      Object.keys(preferences).forEach((poolId) => {
        const poolPreferences = preferences[poolId]

        if (!poolPreferences) {
          return
        }

        this.$set(this.hideEmptyLanesPerPool, poolId, !!poolPreferences.hideEmptyLanes)
        this.$set(this.collapsedPools, poolId, !!poolPreferences.collapsed)

        if (validOrderTypes.includes(poolPreferences.orderType)) {
          this.$set(this.poolOrderTypes, poolId, poolPreferences.orderType)
        }

        if (Array.isArray(poolPreferences.manualOrder)) {
          const poolLanes = this.lanes.filter(lane => lane.pool.pool_id == poolId)
          const currentLaneIds = poolLanes.map(lane => lane.lane_id)
          const restoredOrder = poolPreferences.manualOrder.filter(laneId => currentLaneIds.includes(laneId))
          const newLaneIds = currentLaneIds.filter(laneId => !restoredOrder.includes(laneId))

          this.$set(this.laneManualOrders, poolId, [...restoredOrder, ...newLaneIds])
        }
      })
    },

    fetchJobs(dateRange) {
      if (!dateRange || !Array.isArray(dateRange) || dateRange.length === 0) {
        // Optionally: set loading to false or handle gracefully
        return
      }

      const startDateRange = dateRange[0]
      const endDateRange = dateRange[dateRange.length -1]

      const options = {
        'filter[nodeType]': 'job_instance,job_instance_subtask',
        ...(this.filterShowFinished ? { 'filter[readonly]': true } : {}),
        'filter[startDate]': format(startDateRange.time, 'yyyy-MM-dd HH:mm'),
        'filter[endDate]': format(endDateRange.time, 'yyyy-MM-dd HH:mm')
      }

      this.loading = true

      return this[JobActions.GET_ALL_JOBS](options)
        .finally(() => this.loading = false)
    },

    jobTitle(job) {
      const isSubtask = !!job.parent_job_id

      if (isSubtask) {
        return `${job.job_title} - ${job.fields?.parent_job_title ?? ''}`
      }

      return job.job_title
    },

    jobCardActions(job) {
      const isSubtask = !!job.parent_job_id

      const isMyFocusGroup = this.focusedJobGroup && (this.focusedJobGroup == job.parent_job_id || this.focusedJobGroup == job.job_id)

      let actions = [
        {
          key: 'open_job',
          icon: 'mdi-open-in-new',
          label: isSubtask ? this.$t('open_subtask') : this.$t('open_job') ,
          action: (job) => { this.viewJob(job.job_id) },
        },
        {
          key: 'open_job_folder',
          icon: 'mdi-folder-open-outline',
          label: this.$t('open_job_folder'),
          action: (job) => { this.openJobFilesFolder(job) },
          requiresFolder: true,
        },
        {
          key: 'manage_deliverables',
          icon: 'mdi-package-variant',
          label: this.$t('manage_deliverables'),
          action: (job) => { this.openEditDeliverables(job) },
          // View (256) or Edit (128) deliverables permission
          acl: 128 | 256,
        },
        {
          key: 'deliverable_assets',
          icon: 'mdi-image-multiple-outline',
          label: this.$t('deliverable_assets'),
          action: (job) => { this.openDeliverableAssets(job) },
          // View (256) or Edit (128) deliverables permission
          acl: 128 | 256,
        },
        {
          key: 'send_job',
          icon: 'mdi-send-variant-outline',

          label: isSubtask ? this.$t('send_subtask') : this.$t('send_job'),
          action: (job) => { this.openSendJobDialog(job) },
          hasToBeExecutor: true
        },
        {
          key: 'create_subtask',
          icon: 'mdi-content-copy',
          label: this.$t('create_subtask'),
          action: (job) => { this.openCreateSubtaskDialog(job) },
          acl: 32,
          jobTypes: ['job_instance'],
        },
        {
          key: 'finish_subtask',
          icon: 'mdi-checkbox-outline',
          label: this.$t('finish_subtask'),
          action: (job) => { this.openFinishSubtaskDialog(job) },
          jobTypes: ['job_instance_subtask'],
          hasToBeExecutor: true
        },
        {
          key: 'finish_job',
          icon: 'mdi-checkbox-outline',
          label: this.$t('finish_job'),
          action: (job) => { this.openFinishJobDialog(job) },
          acl: 1024,
          jobTypes: ['job_instance'],
          hasToBeExecutor: true
        },
        {
          key: 'reopen_job',
          icon: 'mdi-lock-open-variant-outline',
          label: this.$t('reopen_job'),
          action: (job) => { this.handleReopenJob(job) },
          acl: 1024,
          jobTypes: ['job_instance'],
          requiresReadonly: true,
          requiresFinishedFilter: true,
        },
        {
          key: 'focus_job',
          icon: isMyFocusGroup ? 'mdi-eye-off-outline' : 'mdi-eye-outline',
          label: isMyFocusGroup ? this.$t('unfocus_job') : this.$t('focus_job'),
          action: (job) => { this.toggleFocusJobGroup(job) },
        },
      ]

      const poolId = this.lanes.find(l => l.lane_id == job.lane_id)?.pool_id

      actions = actions.filter(a =>
        (!a.acl || (a.acl && this.hasAclInPool(a.acl, poolId))) &&
        (!a.jobTypes || (a.jobTypes && a.jobTypes.includes(job.node_type))) &&
        (!a.hasToBeExecutor || (a.hasToBeExecutor && this.canInteractWithJob(job))) &&
        (!a.requiresReadonly || (a.requiresReadonly && job.readonly)) &&
        (!a.requiresFinishedFilter || this.filterShowFinished) &&
        (!a.requiresFolder || (a.requiresFolder && !!job.job_folder_id))
      )


      if (true == job.readonly) {
        actions = actions.filter(a => a.key != 'finish_subtask' && a.key != 'finish_job' && a.key != 'send_job' && a.key != 'create_subtask')
      }

      return actions
    },

    hasAclInPool(acl, poolId) {
      let hasAcl = false

      this.userAcls.filter(pacl => pacl.pool_id == poolId).forEach((poolAcl) => {
        if ((acl & poolAcl.acl) !== 0) {
          hasAcl = true

          return
        }
      })

      return hasAcl
    },

    setSchedule() {
      const schedule = this.appConfig.schedule

      if (! schedule) {
        return
      }

      this.workingSchedule = {
        "Monday": schedule,
        "Tuesday": schedule,
        "Wednesday": schedule,
        "Thursday": schedule,
        "Friday": schedule,
        "Saturday": [],
        "Sunday": []
      }
    },

    // Time and schedule methods
    isWorkingTime(dateTime) {
      const dayName = format(dateTime, 'EEEE')
      const timeString = format(dateTime, 'HH:mm')

      // Check if day has working hours defined
      const daySchedule = this.workingSchedule[dayName]
      if (!daySchedule || daySchedule.length === 0) {
        return false
      }

      // Check if current time falls within any of the working time blocks
      return daySchedule.some(timeBlock => {
        return timeString >= timeBlock.start && timeString <= timeBlock.end
      })
    },

    getFormattedDeadline(job) {
      if (!job.deadline) {
        return
      }

      let date = job.deadline

      if (this.isDateTimeString(date)) {
        date = this.parseDate(date)
      } else {
        date = parseISO(date)
      }

      return format(date, 'dd/MM/yyyy')
    },

    isDateTimeString(dateString) {
      const timeIndicators = /[T\s]\d{2}:\d{2}(:\d{2}(\.\d{3})?)?/; // T or space, HH:MM, optional :SS, optional .SSS
      return timeIndicators.test(dateString);
    },

    parseDate(dateString) {
      if (!dateString) return new Date()

      try {
        // First try standard ISO format
        if (dateString.includes('T')) {
          return parseISO(dateString)
        }

        // Handle format: 2025-09-15 10:00:00+0000
        // This is actually Brazil local time, not UTC despite the +0000
        // Extract date and time parts
        const [datePart, timePart] = dateString.split(' ')
        const [year, month, day] = datePart.split('-').map(Number)
        const [hour, minute, second] = timePart.replace(/\+\d{4}$/, '').split(':').map(Number)

        // Create date in local timezone (Brazil)
        return new Date(year, month - 1, day, hour, minute, second || 0)
      } catch (error) {
        console.warn('Error parsing job date:', error, dateString)
        // Fallback: try to parse as regular date
        return new Date(dateString)
      }
    },

    formatDateRange() {
      if (this.timeColumns.length === 0) return ''
      const start = this.timeColumns[0].time
      const end = this.timeColumns[this.timeColumns.length - 1].time
      return `${format(start, 'MMM dd, HH:mm', { locale: this.currentLocale })} - ${format(end, 'MMM dd, HH:mm', { locale: this.currentLocale })}`
    },

    formatJobTime(dateString) {
      if (!dateString) return ''
      try {
        // Handle the format: 2025-09-15 10:00:00+0000
        const parsedDate = this.parseJobDate(dateString)
        return format(parsedDate, 'MMM dd, HH:mm', { locale: this.currentLocale })
      } catch (error) {
        console.warn('Error formatting job time:', error, dateString)
        return dateString
      }
    },

    formatJobTimeLongDate(dateString) {
      if (!dateString) return ''
      try {
        const parsedDate = this.parseJobDate(dateString)
        return format(parsedDate, 'dd/MM/yyyy HH:mm', { locale: this.currentLocale })
      } catch (error) {
        console.warn('Error formatting job time for dialog:', error, dateString)
        return dateString
      }
    },

    parseJobDate(dateString) {
      if (!dateString) return new Date()

      try {
        // First try standard ISO format
        if (dateString.includes('T')) {
          return parseISO(dateString)
        }

        // Handle format: 2025-09-15 10:00:00+0000
        // This is actually Brazil local time, not UTC despite the +0000
        // Extract date and time parts
        const [datePart, timePart] = dateString.split(' ')
        const [year, month, day] = datePart.split('-').map(Number)
        const [hour, minute, second] = timePart.replace(/\+\d{4}$/, '').split(':').map(Number)

        // Create date in local timezone (Brazil)
        return new Date(year, month - 1, day, hour, minute, second || 0)
      } catch (error) {
        console.warn('Error parsing job date:', error, dateString)
        // Fallback: try to parse as regular date
        return new Date(dateString)
      }
    },

    getJobDefinitionById(id) {
      return this.jobDefinitions.find(jb => jb.job_definition_id == id)
    },

    getPoolLanes(poolId) {
      let poolLanes = this.getPoolLanesFilteredByVisibility(poolId)

      if (this.collapsedPools[poolId]) {
        return []
      }

      // Check if this pool has a custom ordering
      const orderType = this.poolOrderTypes[poolId]

      if (orderType === 'manual' && this.laneManualOrders[poolId]) {
        // Use manual ordering
        const manualOrder = this.laneManualOrders[poolId]
        return manualOrder.map(laneId =>
          poolLanes.find(lane => lane.lane_id === laneId)
        ).filter(Boolean) // Remove any undefined lanes
      } else if (orderType && orderType !== 'manual') {
        // Use automatic ordering
        return this.sortLanesByOrder(poolLanes, orderType)
      }

      return poolLanes
    },

    getPoolLanesFilteredByVisibility(poolId) {
      if (this.isPoolReadOnly(poolId)) return []

      let poolLanes = this.lanes.filter(lane => lane.pool.pool_id == poolId)

      if (this.shouldHideEmptyLanes(poolId)) {
        poolLanes = poolLanes.filter(lane => {
          const laneJobs = this.getLaneJobs(lane.lane_id)
          return laneJobs && laneJobs.length > 0
        })
      }

      return poolLanes
    },

    shouldHideEmptyLanes(poolId) {
      const poolAcls = this.userAcls.filter(acl => acl.pool_id == poolId)
      const VIEW_LANES_WITH_MY_OWN_JOBS = 16
      const viewOnlyLanesWithJobs = poolAcls.some(a => (a.acl & VIEW_LANES_WITH_MY_OWN_JOBS) !== 0)

      return this.hideEmptyLanesPerPool[poolId] || viewOnlyLanesWithJobs
    },

    isPoolReadOnly(poolId) {
      const poolAcl = this.userAcls
        .filter(acl => acl.pool_id == poolId)
        .reduce((permissions, acl) => permissions | Number(acl.acl), 0)

      return poolAcl === 2048
    },

    shouldShowPool(poolId) {
      if (this.isPoolReadOnly(poolId)) return false

      return !this.shouldHideEmptyLanes(poolId)
        || this.getPoolLanesFilteredByVisibility(poolId).length > 0
    },

    // Pool collapse and hide empty lanes methods
    togglePoolCollapse(poolId) {
      this.$set(this.collapsedPools, poolId, !this.collapsedPools[poolId])
      this.$set(this.poolMenus, poolId, false)
      this.saveLanePreferences()
      this.$forceUpdate()
    },

    toggleHideEmptyLanes(poolId) {
      this.$set(this.hideEmptyLanesPerPool, poolId, !this.hideEmptyLanesPerPool[poolId])
      this.$set(this.poolMenus, poolId, false)
      this.saveLanePreferences()
      this.$forceUpdate()
    },

    toggleHideEmptyLanesForAllPools() {
      const shouldHideEmptyLanes = !this.areAllPoolsHidingEmptyLanes

      this.pools.forEach((pool) => {
        this.$set(this.hideEmptyLanesPerPool, pool.pool_id, shouldHideEmptyLanes)
      })

      this.sectorMenu = false
      this.saveLanePreferences()
      this.$forceUpdate()
    },

    toggleCollapseForAllPools() {
      const shouldCollapsePools = !this.areAllPoolsCollapsed

      this.pools.forEach((pool) => {
        this.$set(this.collapsedPools, pool.pool_id, shouldCollapsePools)
      })

      this.sectorMenu = false
      this.saveLanePreferences()
      this.$forceUpdate()
    },

    // Lane ordering methods
    applyLaneOrder(poolId, orderType) {
      const poolLanes = this.lanes.filter(lane => lane.pool.pool_id == poolId)

      if (orderType === 'manual') {
        // Set manual ordering mode for this pool
        this.$set(this.poolOrderTypes, poolId, 'manual')
        // Initialize manual order if not exists
        if (!this.laneManualOrders[poolId]) {
          this.$set(this.laneManualOrders, poolId, poolLanes.map(lane => lane.lane_id))
        }
      } else {
        // Set automatic ordering mode for this pool
        this.$set(this.poolOrderTypes, poolId, orderType)
      }
    },

    orderLanes(poolId, orderType) {
      this.applyLaneOrder(poolId, orderType)

      // Close the menu
      this.$set(this.poolMenus, poolId, false)
      this.saveLanePreferences()

      // Force reactivity update
      this.$forceUpdate()
    },

    orderLanesForAllPools(orderType) {
      this.pools.forEach((pool) => {
        this.applyLaneOrder(pool.pool_id, orderType)
      })

      this.sectorMenu = false
      this.saveLanePreferences()
      this.$forceUpdate()
    },

    // Check if a pool is set to manual ordering
    isManualOrdering(poolId) {
      return this.poolOrderTypes[poolId] === 'manual'
    },

    // Lane drag and drop methods
    handleLaneDragStart(event, lane, poolId) {
      // Only allow dragging if manual ordering is enabled
      if (!this.isManualOrdering(poolId)) {
        event.preventDefault()
        return
      }

      this.draggingLane = lane
      event.dataTransfer.setData('text/plain', JSON.stringify({
        lane_id: lane.lane_id,
        pool_id: poolId
      }))
      event.dataTransfer.effectAllowed = 'move'
    },

    handleLaneDragEnd() {
      this.draggingLane = null
      this.dragOverLaneId = null
    },

    handleLaneDragEnter(event, lane) {
      if (!this.draggingLane || this.draggingLane.lane_id === lane.lane_id) {
        return
      }

      // Only allow dropping within the same pool
      const draggingPoolId = this.pools.find(pool =>
        this.getPoolLanes(pool.pool_id).some(l => l.lane_id === this.draggingLane.lane_id)
      )?.pool_id

      const targetPoolId = this.pools.find(pool =>
        this.getPoolLanes(pool.pool_id).some(l => l.lane_id === lane.lane_id)
      )?.pool_id

      if (draggingPoolId === targetPoolId) {
        // Determine if we should insert before or after based on mouse position
        const rect = event.currentTarget.getBoundingClientRect()
        const mouseY = event.clientY
        const laneMiddle = rect.top + (rect.height / 2)

        this.dragOverLaneId = lane.lane_id
        this.insertAfterTarget = mouseY > laneMiddle
      }
    },

    handleLaneDragLeave(event) {
      // Only clear drag over if we're actually leaving the lane element
      const rect = event.currentTarget.getBoundingClientRect()
      const x = event.clientX
      const y = event.clientY

      if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
        this.dragOverLaneId = null
      }
    },

    handleLaneDrop(event, targetLane) {
      event.preventDefault()

      if (!this.draggingLane) {
        return
      }

      try {
        const dragData = JSON.parse(event.dataTransfer.getData('text/plain'))
        const draggedLaneId = dragData.lane_id
        const poolId = dragData.pool_id

        // Ensure we're dropping within the same pool
        const targetPoolId = this.pools.find(pool =>
          this.getPoolLanes(pool.pool_id).some(l => l.lane_id === targetLane.lane_id)
        )?.pool_id

        if (poolId !== targetPoolId) {
          return
        }

        // Update the manual order for this pool
        this.updateLaneOrder(poolId, draggedLaneId, targetLane.lane_id, this.insertAfterTarget)

      } catch (error) {
        console.error('Error handling lane drop:', error)
      }

      this.draggingLane = null
      this.dragOverLaneId = null
    },

    updateLaneOrder(poolId, draggedLaneId, targetLaneId, insertAfter = false) {
      if (!this.laneManualOrders[poolId]) {
        // Initialize with current order
        const poolLanes = this.lanes.filter(lane => lane.pool.pool_id == poolId)
        this.$set(this.laneManualOrders, poolId, poolLanes.map(lane => lane.lane_id))
      }

      const currentOrder = [...this.laneManualOrders[poolId]]
      const draggedIndex = currentOrder.indexOf(draggedLaneId)
      const targetIndex = currentOrder.indexOf(targetLaneId)

      if (draggedIndex === -1 || targetIndex === -1 || draggedIndex === targetIndex) {
        return
      }

      // Remove dragged item
      currentOrder.splice(draggedIndex, 1)

      // Find where to insert it (after removing the dragged item)
      const newTargetIndex = currentOrder.indexOf(targetLaneId)

      // Insert the dragged item before or after the target
      if (insertAfter) {
        currentOrder.splice(newTargetIndex + 1, 0, draggedLaneId)
      } else {
        currentOrder.splice(newTargetIndex, 0, draggedLaneId)
      }

      // Update the manual order
      this.$set(this.laneManualOrders, poolId, currentOrder)
      this.saveLanePreferences()

      // Force reactivity update
      this.$forceUpdate()
    },

    // Drop zone methods
    handlePoolDropZoneDrop(event, poolId, position) {
      event.preventDefault()

      if (!this.draggingLane) {
        return
      }

      try {
        const dragData = JSON.parse(event.dataTransfer.getData('text/plain'))
        const draggedLaneId = dragData.lane_id
        const dragPoolId = dragData.pool_id

        // Ensure we're dropping within the same pool
        if (poolId !== dragPoolId) {
          return
        }

        // Initialize manual order if not exists
        if (!this.laneManualOrders[poolId]) {
          const poolLanes = this.lanes.filter(lane => lane.pool.pool_id == poolId)
          this.$set(this.laneManualOrders, poolId, poolLanes.map(lane => lane.lane_id))
        }

        const currentOrder = [...this.laneManualOrders[poolId]]
        const draggedIndex = currentOrder.indexOf(draggedLaneId)

        if (draggedIndex === -1) {
          return
        }

        // Remove dragged item
        currentOrder.splice(draggedIndex, 1)

        // Insert at top or bottom based on position
        if (position === 'top') {
          currentOrder.unshift(draggedLaneId)
        } else {
          currentOrder.push(draggedLaneId)
        }

        // Update the manual order
        this.$set(this.laneManualOrders, poolId, currentOrder)
        this.saveLanePreferences()

        // Force reactivity update
        this.$forceUpdate()

      } catch (error) {
        console.error('Error handling drop zone drop:', error)
      }

      this.draggingLane = null
      this.dragOverLaneId = null
      this.dragOverDropZone = null
    },

    handleDropZoneDragEnter(event, position) {
      if (!this.draggingLane) {
        return
      }
      this.dragOverDropZone = position
    },

    handleDropZoneDragLeave() {
      this.dragOverDropZone = null
    },

    sortLanesByOrder(lanes, orderType) {
      if (!orderType || orderType === 'manual') {
        return lanes
      }

      return [...lanes].sort((laneA, laneB) => {
        const jobsA = this.getLaneJobs(laneA.lane_id)
        const jobsB = this.getLaneJobs(laneB.lane_id)

        // Get the first job for each lane (earliest by start date)
        const firstJobA = this.getFirstJobInLane(jobsA)
        const firstJobB = this.getFirstJobInLane(jobsB)

        // If one lane has no jobs, put it at the end
        if (!firstJobA && !firstJobB) return 0
        if (!firstJobA) return 1
        if (!firstJobB) return -1

        let dateA, dateB

        switch (orderType) {
          case 'start_date':
            dateA = this.parseJobDate(firstJobA.start_date)
            dateB = this.parseJobDate(firstJobB.start_date)
            break
          case 'end_date':
            dateA = this.parseJobDate(firstJobA.end_date)
            dateB = this.parseJobDate(firstJobB.end_date)
            break
          case 'deadline':
            dateA = this.parseJobDate(firstJobA.deadline)
            dateB = this.parseJobDate(firstJobB.deadline)
            break
          default:
            return 0
        }

        return dateA - dateB
      })
    },

    getFirstJobInLane(jobs) {
      if (!jobs || jobs.length === 0) return null

      // Sort jobs by start date and return the first one
      return jobs.sort((a, b) => {
        const dateA = this.parseJobDate(a.start_date)
        const dateB = this.parseJobDate(b.start_date)
        return dateA - dateB
      })[0]
    },

    // Job management methods
    getLaneJobs(laneId) {
      let jobs = this.filteredJobs.filter(job => job.lane_id == laneId)

      const lane = this.lanes.find(l => l.lane_id == laneId)
      const poolAcls = this.userAcls.filter(acl => acl.pool_id == lane.pool_id)

      const VIEW_WITH_DETAILS = 4
      if (poolAcls.some(a => (a.acl & VIEW_WITH_DETAILS) !== 0)) {
        return jobs
      }

      const VIEW_WITHOUT_DETAILS = 8
      const VIEW_WITH_MY_OWN_JOBS = 16
      if (poolAcls.some(a => (a.acl & VIEW_WITHOUT_DETAILS) !== 0 || (a.acl & VIEW_WITH_MY_OWN_JOBS) !== 0)) {
        return jobs.filter(j => j.view_only == false)
      }

      return []
    },

    getLaneHeight(laneId) {
      // In hours mode, use fixed height
      if (this.timelineViewMode === 'hours') {
        return this.laneRowHeight
      }

      // In days mode, calculate height based on maximum stacked jobs
      const laneJobs = this.getLaneJobs(laneId)
      if (!laneJobs || laneJobs.length === 0) {
        return this.laneRowHeight
      }

      let maxStackedJobs = 1

      // For each day in the timeline, find the maximum number of overlapping jobs
      this.timeColumns.forEach(column => {
        const columnDate = new Date(column.time)
        columnDate.setHours(0, 0, 0, 0)

        const jobsOnThisDay = laneJobs.filter(job => {
          const startDateTime = this.parseJobDate(job.start_date)
          const endDateTime = this.parseJobDate(job.end_date)

          const startDate = new Date(startDateTime)
          startDate.setHours(0, 0, 0, 0)

          const endDate = new Date(endDateTime)
          endDate.setHours(23, 59, 59, 999)

          // Check if job spans through this day
          return (endDate >= columnDate && startDate <= columnDate)
        })

        if (jobsOnThisDay.length > maxStackedJobs) {
          maxStackedJobs = jobsOnThisDay.length
        }
      })

      // Calculate height: base height + additional height for stacked jobs
      const baseHeight = this.laneRowHeight
      const stackHeight = 25 // Height per stacked job (matches the stackOffset in getJobStyleForDaysView)
      const additionalHeight = (maxStackedJobs - 1) * stackHeight

      return baseHeight + additionalHeight
    },

    getJobStyle(job, currentLaneId) {
      if (!job.start_date || !job.end_date) {
        return { display: 'none' }
      }

      try {
        const startDateTime = this.parseJobDate(job.start_date)
        const endDateTime = this.parseJobDate(job.end_date)

        // Ensure we have valid time columns
        if (!this.timeColumns || this.timeColumns.length === 0) {
          return { display: 'none' }
        }

        let style
        if (this.timelineViewMode === 'days') {
          style = this.getJobStyleForDaysView(job, startDateTime, endDateTime)
        } else {
          style = this.getJobStyleForHoursView(job, startDateTime, endDateTime)
        }

        // If this job is being dragged to a different lane, show it in the target lane
        if (this.draggingJob && this.draggingJob.job_id === job.job_id && this.targetLaneId && this.targetLaneId !== job.lane_id) {
          // Find the target lane index
          const targetLaneIndex = this.lanes.findIndex(lane => lane.id === this.targetLaneId)
          const currentLaneIndex = this.lanes.findIndex(lane => lane.id === currentLaneId)

          if (targetLaneIndex !== -1 && currentLaneIndex !== -1) {
            // If this is the target lane, show the job
            if (currentLaneId === this.targetLaneId) {
              style.opacity = '0.7' // Semi-transparent to show it's being moved
              style.border = '2px dashed #fff'
            } else if (currentLaneId === job.lane_id) {
              // Hide the job in its original lane during cross-lane drag
              style.opacity = '0.3'
            }
          }
        }

        return style
      } catch (error) {
        console.error('Error calculating job style:', error)
        return { display: 'none' }
      }
    },

    getJobStyleForDaysView(job, startDateTime, endDateTime) {
      // Find which day columns the job spans
      let startColumnIndex = -1
      let endColumnIndex = -1

      for (let i = 0; i < this.timeColumns.length; i++) {
        const columnDate = new Date(this.timeColumns[i].time)
        columnDate.setHours(0, 0, 0, 0)

        const nextColumnDate = i < this.timeColumns.length - 1
          ? new Date(this.timeColumns[i + 1].time)
          : new Date(columnDate.getTime() + (24 * 60 * 60 * 1000))
        nextColumnDate.setHours(0, 0, 0, 0)

        const startDate = new Date(startDateTime)
        startDate.setHours(0, 0, 0, 0)

        const endDate = new Date(endDateTime)
        endDate.setHours(23, 59, 59, 999)

        // Check if job starts on this day
        if (startColumnIndex === -1 && startDate.getTime() === columnDate.getTime()) {
          startColumnIndex = i
        }

        // Check if job ends on this day or spans through this day
        if (endDate >= columnDate && startDate <= columnDate) {
          endColumnIndex = i
        }
      }

      // If job is completely outside visible timeline, hide it
      if (startColumnIndex === -1 && endColumnIndex === -1) {
        return { display: 'none' }
      }

      // Calculate position and width
      let left = 0
      let width = this.columnWidth

      if (startColumnIndex !== -1) {
        left = startColumnIndex * this.columnWidth
      } else {
        left = 0
      }

      if (endColumnIndex !== -1 && startColumnIndex !== -1) {
        width = ((endColumnIndex - startColumnIndex + 1) * this.columnWidth) - 4 // 4px margin
      } else if (endColumnIndex !== -1) {
        width = ((endColumnIndex + 1) * this.columnWidth) - left - 4
      } else if (startColumnIndex !== -1) {
        width = (this.timeColumns.length * this.columnWidth) - left - 4
      }

      width = Math.max(width, 30) // Minimum width

      // Calculate stacking position for multiple jobs on same days
      const laneJobs = this.getLaneJobs(job.lane_id)
      const overlappingJobs = laneJobs.filter(otherJob => {
        if (otherJob.job_id === job.job_id) return false

        const otherStart = this.parseJobDate(otherJob.start_date)
        const otherEnd = this.parseJobDate(otherJob.end_date)
        otherStart.setHours(0, 0, 0, 0)
        otherEnd.setHours(23, 59, 59, 999)

        const jobStart = new Date(startDateTime)
        const jobEnd = new Date(endDateTime)
        jobStart.setHours(0, 0, 0, 0)
        jobEnd.setHours(23, 59, 59, 999)

        return (jobStart <= otherEnd && jobEnd >= otherStart)
      })

      // Sort overlapping jobs by job_id to ensure consistent stacking order
      overlappingJobs.sort((a, b) => a.job_id - b.job_id)

      // Find the position of current job among all overlapping jobs (including itself)
      const allOverlappingJobs = [...overlappingJobs, job].sort((a, b) => a.job_id - b.job_id)
      const stackIndex = allOverlappingJobs.findIndex(j => j.job_id === job.job_id)
      const stackOffset = stackIndex * 25

      // Color based on status
      const statusColors = {
        pending: '#FFC107',
        in_progress: '#2196F3',
        completed: '#4CAF50',
        on_hold: '#FF5722',
        draft: '#9E9E9E'
      }

      const style = {
        left: (left + 2) + 'px',
        width: width + 'px',
        backgroundColor: statusColors[job.status] || statusColors.pending,
        top: (10 + stackOffset) + 'px',
        zIndex: 5 + stackOffset
      }

      // Keep expanded height while dragging this card
      const isDraggingThis = this.draggingJob && this.draggingJob.job_id === job.job_id
      if (isDraggingThis) {
        style.height = 'auto'
        style.width = 'auto'
        style.zIndex = 10000
      } else {
        style.height = '20px'
      }

      return style
    },

    getJobStyleForHoursView(job, startDateTime, endDateTime) {
      // Calculate working hours duration for this job
      const workingHoursDuration = this.calculateWorkingHoursDuration(startDateTime, endDateTime)

      // Check if job overlaps with visible timeline
      const timelineStart = this.timeColumns[0].time
      const timelineEnd = this.timeColumns[this.timeColumns.length - 1].time

      // If job is completely outside visible timeline, hide it
      if (endDateTime < timelineStart || startDateTime > timelineEnd) {
        return { display: 'none' }
      }

      // Calculate the actual start position (can be negative if job starts before timeline)
      let left = 0
      let isPartiallyVisible = false

      if (startDateTime < timelineStart) {
        // Job starts before visible timeline - calculate negative left position using working hours
        const workingHoursBeforeTimeline = this.calculateWorkingHoursDuration(startDateTime, timelineStart)
        const pixelsPerWorkingMinute = this.columnWidth / 60
        left = -(workingHoursBeforeTimeline * pixelsPerWorkingMinute)
        isPartiallyVisible = true
      } else {
        // Job starts within visible timeline - find exact position
        for (let i = 0; i < this.timeColumns.length; i++) {
          const columnTime = this.timeColumns[i].time
          const nextColumnTime = i < this.timeColumns.length - 1
            ? this.timeColumns[i + 1].time
            : new Date(columnTime.getTime() + (60 * 60 * 1000)) // Add 1 hour

          if (startDateTime >= columnTime && startDateTime < nextColumnTime) {
            // Calculate exact position within the column
            const minutesIntoColumn = (startDateTime - columnTime) / (1000 * 60)
            const pixelsIntoColumn = (minutesIntoColumn / 60) * this.columnWidth
            left = (i * this.columnWidth) + pixelsIntoColumn
            break
          }
        }
      }

      // Calculate width based on working hours duration
      const workingMinutes = workingHoursDuration
      const pixelsPerWorkingMinute = this.columnWidth / 60
      let width = Math.max(workingMinutes * pixelsPerWorkingMinute, 30) // Minimum width of 30px

      // Color based on status
      const statusColors = {
        pending: '#FFC107',
        in_progress: '#2196F3',
        completed: '#4CAF50',
        on_hold: '#FF5722',
        draft: '#9E9E9E'
      }

      const style = {
        left: left + 'px',
        width: width + 'px',
        backgroundColor: statusColors[job.status] || statusColors.pending,
        top: '5px',
        height: '75px'
      }

      // Add special styling for partially visible jobs
      if (isPartiallyVisible || false == this.canInteractWithJob(job)) {
        style.cursor = 'default'
      }

      return style
    },

    canInteractWithJob(job) {
      if (job.view_only || job.readonly) {
        return false
      }

      return this.userLanes.includes(job.lane_id)
    },

    getUserName(usersId) {
      return this.users.find(user => user.users_id == usersId)?.name
    },

    getDeliverablesCount(deliverables) {
      if (!deliverables || deliverables?.length == 0) {
        return 0
      }

      return deliverables.reduce((accumulator, deliverable) => {
        return accumulator + (deliverable.quantity || 1)
      }, 0)
    },

    viewJob(jobId) {
      this.$emit('view-job', jobId)
    },

    openEditDeliverables(job) {
      this.$router.push({
        name: 'JobDeliverableManagement',
        params: {
          appId: this.$route.params.appId,
          jobId: job.job_id,
          businessAppId: this.$route.params.businessAppId
        }
      })
    },

    openDeliverableAssets(job) {
      const contentAppId = this.appConfig?.content_app_inst_id || this.appConfig?.content_app_id
      const businessAppId = this.appConfig?.business_app_inst_id || this.appConfig?.business_app_id
      this.$router.push({
        name: 'JobDeliverableAssets',
        params: {
          appId: this.$route.params.appId,
          jobId: job.job_id,
          contentAppId,
          businessAppId
        }
      })
    },

    openJobFilesFolder(job) {
      const contentsId = this.$route.params.contentAppId ||
        this.appConfig?.content_app_inst_id ||
        this.appConfig?.content_app_id || null

      const jobFolderId = job?.job_folder_id

      if (!contentsId || !jobFolderId) return

      const { href } = this.$router.resolve({
        name: 'ContentNavigation',
        params: { appId: contentsId, parentId: jobFolderId },
      })

      window.open(href, '_blank', 'noopener')
    },

    getJobPunctualityClass(job) {
      const status = this.getStatusById(job.status_id)

      // Removendo timezone offset das strings de data
      const jobStartDate = new Date(job.start_date.replace(/\+\d{4}$/, ""))
      const jobEndDate = new Date(job.end_date.replace(/\+\d{4}$/, ""))
      const jobDeadline = new Date(job.deadline?.replace(/\+\d{4}$/, ""))

      if (job.view_only) {
        return 'job-occupied'
      }

      if (status.status_type == "waiting" && jobStartDate > this.currentTime) {
        return 'job-waiting'
      }

      if (status.status_type == "done" || true == job.readonly) {
        return 'job-done'
      }

      if (status.status_type == "canceled") {
        return 'job-canceled'
      }

      if (status.status_type == "doing" && jobEndDate > this.currentTime) {
        return 'job-doing'
      }

      if (
        status.status_type == "waiting"
        && jobStartDate <= this.currentTime
        && jobEndDate > this.currentTime
      ) {
        return 'job-start-date-late'
      }

      if (
        (status.status_type == "doing" || status.status_type == "waiting")
        && jobDeadline <= this.currentTime
        && job.deadline
      ) {
        return 'job-deadline-late'
      }

      if (
        (status.status_type == "doing" || status.status_type == "waiting")
        && jobEndDate <= this.currentTime
      ) {
        return 'job-end-date-late'
      }

      return ''
    },

    getStatusById(statusId) {
      return this.statuses.find(s => s.status_id == statusId)
    },

    // Header action button methods
    goToPreviousDateUnit() {
      if (this.timelineViewMode === 'hours') {
        // Find the previous working hour
        const newTimelineTime = this.findPreviousWorkingHour(this.timelineTime)
        this.timelineTime = newTimelineTime
      } else {
        // Move back by one day (for days view, we already filter to working days)
        const newTimelineTime = new Date(this.timelineTime)
        newTimelineTime.setDate(newTimelineTime.getDate() - 1)
        this.timelineTime = newTimelineTime
      }
    },

    goToNextDateUnit() {
      if (this.timelineViewMode === 'hours') {
        // Find the next working hour
        const newTimelineTime = this.findNextWorkingHour(this.timelineTime)
        this.timelineTime = newTimelineTime
      } else {
        // Move forward by one day (for days view, we already filter to working days)
        const newTimelineTime = new Date(this.timelineTime)
        newTimelineTime.setDate(newTimelineTime.getDate() + 1)
        this.timelineTime = newTimelineTime
      }
    },

    goToPreviousGroupDateUnit() {
      if (this.timelineViewMode === 'hours') {
        // Move back by columnsCount working hours
        let newTimelineTime = new Date(this.timelineTime)
        for (let i = 0; i < this.columnsCount; i++) {
          newTimelineTime = this.findPreviousWorkingHour(newTimelineTime)
        }
        this.timelineTime = newTimelineTime
      } else {
        // Move back by columnsCount days
        const newTimelineTime = new Date(this.timelineTime)
        newTimelineTime.setDate(newTimelineTime.getDate() - this.columnsCount)
        this.timelineTime = newTimelineTime
      }
    },

    goToNextGroupDateUnit() {
      if (this.timelineViewMode === 'hours') {
        // Move forward by columnsCount working hours
        let newTimelineTime = new Date(this.timelineTime)
        for (let i = 0; i < this.columnsCount; i++) {
          newTimelineTime = this.findNextWorkingHour(newTimelineTime)
        }
        this.timelineTime = newTimelineTime
      } else {
        // Move forward by columnsCount days
        const newTimelineTime = new Date(this.timelineTime)
        newTimelineTime.setDate(newTimelineTime.getDate() + this.columnsCount)
        this.timelineTime = newTimelineTime
      }
    },

    // Drag and drop functionality
    startDrag(job, event) {
      // Don't start drag if clicking on resize handles or job actions
      if (event.target.classList.contains('resize-handle') ||
          event.target.closest('.resize-handle') ||
          event.target.closest('.job-actions') ||
          false == this.canInteractWithJob(job)
      ) {
        return
      }

      this.draggingJob = job
      this.dragStartX = event.clientX
      this.dragStartY = event.clientY
      this.dragStartTime = this.parseJobDate(job.start_date)
      this.containerRect = this.$refs.timelineBody.getBoundingClientRect()

      // Store original job data for potential reversion
      this.draggingJob.originalStartDate = job.start_date
      this.draggingJob.originalEndDate = job.end_date
      this.draggingJob.originalLaneId = job.lane_id

      // Calculate the offset from where the user clicked on the job card
      const jobCard = event.currentTarget
      const jobRect = jobCard.getBoundingClientRect()
      this.dragOffsetX = event.clientX - jobRect.left
      this.dragOffsetY = event.clientY - jobRect.top

      event.preventDefault()
      event.stopPropagation()
    },

    handleDragEnd() {
      // Call the same logic as handleMouseUp to ensure drop is processed
      this.handleMouseUp()
    },

    startResize(job, direction, event) {
      this.resizingJob = job
      this.resizeDirection = direction
      this.dragStartX = event.clientX
      this.containerRect = this.$refs.timelineBody.getBoundingClientRect()

      // Store original job data for potential reversion
      this.resizingJob.originalStartDate = job.start_date
      this.resizingJob.originalEndDate = job.end_date

      event.preventDefault()
      event.stopPropagation()
    },

    handleMouseMove(event) {
      if (this.draggingJob && !this.resizingJob) {
        this.handleScrollScreenOnDragCard(event)
        this.handleJobDragMove(event)
      } else if (this.resizingJob && !this.draggingJob) {
        this.handleJobResizeMove(event)
      }
    },

    handleJobDragMove(event) {
      if (!this.draggingJob || !this.dragStartTime) return

      // Store original lane_id if not already stored
      if (!this.draggingJob.originalLaneId) {
        this.draggingJob.originalLaneId = this.draggingJob.lane_id
      }

      // Detect which lane we're over based on vertical position
      const timelineBodyRect = this.$refs.timelineBody.getBoundingClientRect()
      const adjustedMouseY = event.clientY - this.dragOffsetY
      const relativeY = adjustedMouseY - timelineBodyRect.top

      // Create a flat list of all lanes from all pools to match the visual order
      const allLanes = []
      this.pools.forEach(pool => {
        const poolLanes = this.getPoolLanes(pool.pool_id)
        allLanes.push(...poolLanes)
      })

      // Calculate which lane we're over by accumulating lane heights
      let currentY = 0
      let targetLaneIndex = -1

      for (let i = 0; i < allLanes.length; i++) {
        const laneHeight = this.getLaneHeight(allLanes[i].id)
        if (relativeY >= currentY && relativeY < currentY + laneHeight) {
          targetLaneIndex = i
          break
        }
        currentY += laneHeight
      }

      // Update target lane if we're over a different lane
      if (targetLaneIndex >= 0 && targetLaneIndex < allLanes.length) {
        this.targetLaneId = allLanes[targetLaneIndex].lane_id
        // Temporarily update the job's lane_id to make it appear in the target lane
        this.draggingJob.lane_id = this.targetLaneId

        // Find which pool this lane belongs to and set target pool
        const targetLane = allLanes[targetLaneIndex]
        const targetPool = this.pools.find(pool =>
          this.getPoolLanes(pool.pool_id).some(lane => lane.lane_id === targetLane.lane_id)
        )
        this.targetPoolId = targetPool ? targetPool.pool_id : null
      }

      // Calculate which column we're over based on mouse position, accounting for drag offset
      const laneTimelineRect = this.$refs.laneTimeline[0].getBoundingClientRect()
      const adjustedMouseX = event.clientX - this.dragOffsetX // Adjust mouse position by drag offset
      const relativeX = adjustedMouseX - laneTimelineRect.left // Use lane timeline left, not timeline body
      const columnIndex = Math.floor(relativeX / this.columnWidth)

      // Ensure we're within valid column bounds
      if (columnIndex < 0 || columnIndex >= this.timeColumns.length) {
        return
      }

      // Get the target column time
      const targetColumn = this.timeColumns[columnIndex]
      if (!targetColumn) return

      if (this.timelineViewMode === 'days') {
        // Day mode: preserve original hours and fit into new day
        this.handleDayModeDrag(targetColumn)
      } else {
        // Hour mode: original logic
        this.handleHourModeDrag(targetColumn, relativeX, columnIndex)
      }
    },

    handleDayModeDrag(targetColumn) {
      // Get original job times
      const originalStartDateTime = this.parseJobDate(this.draggingJob.originalStartDate || this.draggingJob.start_date)
      const originalEndDateTime = this.parseJobDate(this.draggingJob.originalEndDate || this.draggingJob.end_date)

      // Extract original hours and minutes
      const originalStartHour = originalStartDateTime.getHours()
      const originalStartMinute = originalStartDateTime.getMinutes()
      const originalEndHour = originalEndDateTime.getHours()
      const originalEndMinute = originalEndDateTime.getMinutes()

      // Create new start time with target day but original hours
      const newStartDateTime = new Date(targetColumn.time)
      newStartDateTime.setHours(originalStartHour, originalStartMinute, 0, 0)

      // Create new end time with target day but original hours
      const newEndDateTime = new Date(targetColumn.time)
      newEndDateTime.setHours(originalEndHour, originalEndMinute, 0, 0)

      // If the job spans multiple days, adjust the end date
      const originalDuration = originalEndDateTime.getTime() - originalStartDateTime.getTime()
      const daysDuration = Math.floor(originalDuration / (24 * 60 * 60 * 1000))

      if (daysDuration > 0) {
        newEndDateTime.setDate(newEndDateTime.getDate() + daysDuration)
      }

      // Check if this is actually a different time slot to avoid unnecessary updates
      const currentStartDateTime = this.parseJobDate(this.draggingJob.start_date)
      if (newStartDateTime.getTime() === currentStartDateTime.getTime()) {
        return // No change needed
      }

      // Validate that the new times fit within working schedule
      if (this.isValidJobTimeRange(newStartDateTime, newEndDateTime)) {
        // Update the job object directly for immediate visual feedback
        this.draggingJob.start_date = format(newStartDateTime, 'yyyy-MM-dd HH:mm:ss')
        this.draggingJob.end_date = format(newEndDateTime, 'yyyy-MM-dd HH:mm:ss')
      } else {
        // Try to fit the job into the nearest valid time slot on the target day
        const adjustedTimes = this.fitJobIntoWorkingDay(targetColumn.time, originalStartDateTime, originalEndDateTime)
        if (adjustedTimes) {
          this.draggingJob.start_date = adjustedTimes.start_date
          this.draggingJob.end_date = adjustedTimes.end_date
        }
      }

      // Force reactivity update
      this.$forceUpdate()
    },

    handleHourModeDrag(targetColumn, relativeX, columnIndex) {
      // Calculate position within the column (0-1)
      const columnStartX = columnIndex * this.columnWidth
      const columnOffset = Math.max(0, Math.min(1, (relativeX - columnStartX) / this.columnWidth))

      // Calculate minutes into the target hour (0-59)
      const minutesIntoHour = columnOffset * 60

      // Snap to 15-minute intervals with better tolerance
      const snappedMinutes = Math.round(minutesIntoHour / 15) * 15

      // Create the new start time
      const newStartDateTime = new Date(targetColumn.time)
      newStartDateTime.setMinutes(snappedMinutes, 0, 0)

      // Check if this is actually a different time slot to avoid unnecessary updates
      const currentStartDateTime = this.parseJobDate(this.draggingJob.start_date)
      if (newStartDateTime.getTime() === currentStartDateTime.getTime()) {
        return // No change needed
      }

      // Calculate the original working hours duration
      const originalStartDateTime = this.parseJobDate(this.draggingJob.start_date)
      const originalEndDateTime = this.parseJobDate(this.draggingJob.end_date)
      const workingHoursDuration = this.calculateWorkingHoursDuration(originalStartDateTime, originalEndDateTime)

      // Calculate new end time based on working hours duration
      const newEndDateTime = this.addWorkingHoursDuration(newStartDateTime, workingHoursDuration)

      // Update the job object directly for immediate visual feedback
      this.draggingJob.start_date = format(newStartDateTime, 'yyyy-MM-dd HH:mm:ss')
      this.draggingJob.end_date = format(newEndDateTime, 'yyyy-MM-dd HH:mm:ss')

      // Force reactivity update
      this.$forceUpdate()
    },

    handleScrollScreenOnDragCard(event) {
      // Auto-scroll the timeline container when near its edges during drag
      const container = this.$refs.timelineContainer
      if (!container) return

      const rect = container.getBoundingClientRect()
      const threshold = 60 // Pixels from the container edge to trigger scroll
      const speed = 20 // Pixels per mousemove to scroll

      // Vertical
      if (event.clientY < rect.top + threshold) {
        container.scrollTop -= speed
      } else if (event.clientY > rect.bottom - threshold) {
        container.scrollTop += speed
      }

      // Horizontal
      if (event.clientX < rect.left + threshold) {
        container.scrollLeft -= speed
      } else if (event.clientX > rect.right - threshold) {
        container.scrollLeft += speed
      }
    },

    fitJobIntoWorkingDay(targetDay, originalStartDateTime, originalEndDateTime) {
      const targetDate = new Date(targetDay)
      const dayName = format(targetDate, 'EEEE')
      const daySchedule = this.workingSchedule[dayName]

      if (!daySchedule || daySchedule.length === 0) {
        return null // No working hours for this day
      }

      // Calculate original job duration in minutes
      const originalDuration = (originalEndDateTime.getTime() - originalStartDateTime.getTime()) / (1000 * 60)

      // Get original start time
      const originalStartHour = originalStartDateTime.getHours()
      const originalStartMinute = originalStartDateTime.getMinutes()
      const originalStartTimeString = `${originalStartHour.toString().padStart(2, '0')}:${originalStartMinute.toString().padStart(2, '0')}`

      // Try to fit the job at the original time first
      for (const timeBlock of daySchedule) {
        const blockStartMinutes = this.timeToMinutes(timeBlock.start)
        const blockEndMinutes = this.timeToMinutes(timeBlock.end)
        const originalStartMinutes = this.timeToMinutes(originalStartTimeString)

        // Check if original time fits within this block
        if (originalStartMinutes >= blockStartMinutes &&
            originalStartMinutes + originalDuration <= blockEndMinutes) {

          const newStartDateTime = new Date(targetDate)
          newStartDateTime.setHours(originalStartHour, originalStartMinute, 0, 0)

          const newEndDateTime = new Date(newStartDateTime.getTime() + (originalDuration * 60 * 1000))

          return {
            start_date: newStartDateTime.toISOString(),
            end_date: newEndDateTime.toISOString()
          }
        }
      }

      // If original time doesn't fit, try to find the best alternative
      let bestFit = null
      let minDistance = Infinity

      for (const timeBlock of daySchedule) {
        const blockStartMinutes = this.timeToMinutes(timeBlock.start)
        const blockEndMinutes = this.timeToMinutes(timeBlock.end)

        // Check if job duration fits in this block
        if (originalDuration <= (blockEndMinutes - blockStartMinutes)) {
          // Try different positions within this block (every 15 minutes)
          for (let startMinutes = blockStartMinutes; startMinutes <= blockEndMinutes - originalDuration; startMinutes += 15) {
            const distance = Math.abs(startMinutes - this.timeToMinutes(originalStartTimeString))

            if (distance < minDistance) {
              minDistance = distance

              const hours = Math.floor(startMinutes / 60)
              const minutes = startMinutes % 60

              const newStartDateTime = new Date(targetDate)
              newStartDateTime.setHours(hours, minutes, 0, 0)

              const newEndDateTime = new Date(newStartDateTime.getTime() + (originalDuration * 60 * 1000))

              bestFit = {
                start_date: newStartDateTime.toISOString(),
                end_date: newEndDateTime.toISOString()
              }
            }
          }
        }
      }

      return bestFit
    },

    handleJobResizeMove(event) {
      if (!this.resizingJob) return

      // Calculate which column we're over based on mouse position
      // Use the timeline body as reference instead of laneTimeline array
      const timelineBodyRect = this.$refs.timelineBody.getBoundingClientRect()
      const headerWidth = 200 // Job labels column width
      const relativeX = event.clientX - timelineBodyRect.left - headerWidth
      const columnIndex = Math.floor(relativeX / this.columnWidth)

      // Ensure we're within valid column bounds
      if (columnIndex < 0 || columnIndex >= this.timeColumns.length) {
        return
      }

      // Get the target column time
      const targetColumn = this.timeColumns[columnIndex]
      if (!targetColumn) return

      if (this.timelineViewMode === 'days') {
        // Day mode: handle resize with working day validation
        this.handleDayModeResize(targetColumn)
      } else {
        // Hour mode: handle resize with working hour validation
        this.handleHourModeResize(targetColumn, relativeX, columnIndex)
      }
    },

    handleDayModeResize(targetColumn) {
      const targetDate = new Date(targetColumn.time)

      if (this.resizeDirection === 'left') {
        // Resizing the start time (left edge) - move start date to target day
        const originalEndDateTime = this.parseJobDate(this.resizingJob.end_date)
        const originalStartDateTime = this.parseJobDate(this.resizingJob.originalStartDate || this.resizingJob.start_date)

        // Preserve original hours and minutes for start time
        const newStartDateTime = new Date(targetDate)
        newStartDateTime.setHours(originalStartDateTime.getHours(), originalStartDateTime.getMinutes(), 0, 0)

        // Ensure the new start time is before the end time
        if (newStartDateTime >= originalEndDateTime) {
          return
        }

        // Validate that the new time range is valid
        if (this.isValidJobTimeRange(newStartDateTime, originalEndDateTime)) {
          this.resizingJob.start_date = format(newStartDateTime, 'yyyy-MM-dd HH:mm:ss')
          this.$forceUpdate()
        } else {
          // Try to fit the start time into the target day's working hours
          const adjustedStartTime = this.fitTimeIntoWorkingDay(targetDate, originalStartDateTime)
          if (adjustedStartTime && adjustedStartTime < originalEndDateTime) {
            this.resizingJob.start_date = format(adjustedStartTime, 'yyyy-MM-dd HH:mm:ss')
            this.$forceUpdate()
          }
        }

      } else if (this.resizeDirection === 'right') {
        // Resizing the end time (right edge) - move end date to target day
        const originalStartDateTime = this.parseJobDate(this.resizingJob.start_date)
        const originalEndDateTime = this.parseJobDate(this.resizingJob.originalEndDate || this.resizingJob.end_date)

        // Preserve original hours and minutes for end time
        const newEndDateTime = new Date(targetDate)
        newEndDateTime.setHours(originalEndDateTime.getHours(), originalEndDateTime.getMinutes(), 0, 0)

        // Ensure the new end time is after the start time
        if (newEndDateTime <= originalStartDateTime) {
          return
        }

        // Validate that the new time range is valid
        if (this.isValidJobTimeRange(originalStartDateTime, newEndDateTime)) {
          this.resizingJob.end_date = format(newEndDateTime, 'yyyy-MM-dd HH:mm:ss')
          this.$forceUpdate()
        } else {
          // Try to fit the end time into the target day's working hours
          const adjustedEndTime = this.fitTimeIntoWorkingDay(targetDate, originalEndDateTime)
          if (adjustedEndTime && adjustedEndTime > originalStartDateTime) {
            this.resizingJob.end_date = format(adjustedEndTime, 'yyyy-MM-dd HH:mm:ss')
            this.$forceUpdate()
          }
        }
      }
    },

    handleHourModeResize(targetColumn, relativeX, columnIndex) {
      // Calculate position within the column (0-1)
      const columnStartX = columnIndex * this.columnWidth
      const columnOffset = Math.max(0, Math.min(1, (relativeX - columnStartX) / this.columnWidth))

      // Calculate minutes into the target hour (0-59)
      const minutesIntoHour = columnOffset * 60

      // Snap to 15-minute intervals
      const snappedMinutes = Math.round(minutesIntoHour / 15) * 15

      // Create the target time
      const targetDateTime = new Date(targetColumn.time)
      targetDateTime.setMinutes(snappedMinutes, 0, 0)

      if (this.resizeDirection === 'left') {
        // Resizing the start time (left edge)
        const originalEndDateTime = this.parseJobDate(this.resizingJob.end_date)

        // Ensure the new start time is before the end time
        if (targetDateTime >= originalEndDateTime) {
          return
        }

        // Calculate working hours duration from new start to original end

        const newWorkingDuration = this.calculateWorkingHoursDuration(targetDateTime, originalEndDateTime)

        // Only update if we have a valid working duration (at least 15 minutes)

        if (newWorkingDuration >= 15) {

          this.resizingJob.start_date = targetDateTime.toISOString()

          this.$forceUpdate()
        }
      } else if (this.resizeDirection === 'right') {
        // Resizing the end time (right edge)
        const originalStartDateTime = this.parseJobDate(this.resizingJob.start_date)

        // Ensure the new end time is after the start time
        if (targetDateTime <= originalStartDateTime) {
          return
        }

        // Calculate working hours duration from original start to new end

        const newWorkingDuration = this.calculateWorkingHoursDuration(originalStartDateTime, targetDateTime)

        // Only update if we have a valid working duration (at least 15 minutes)

        if (newWorkingDuration >= 15) {

          this.resizingJob.end_date = targetDateTime.toISOString()

          this.$forceUpdate()

        }
      }
    },

    fitTimeIntoWorkingDay(targetDay, originalDateTime) {
      const targetDate = new Date(targetDay)
      const dayName = format(targetDate, 'EEEE')
      const daySchedule = this.workingSchedule[dayName]

      if (!daySchedule || daySchedule.length === 0) {
        return null // No working hours for this day
      }

      // Get original time
      const originalHour = originalDateTime.getHours()
      const originalMinute = originalDateTime.getMinutes()
      const originalTimeString = `${originalHour.toString().padStart(2, '0')}:${originalMinute.toString().padStart(2, '0')}`

      // Try to fit the time at the original hour first
      for (const timeBlock of daySchedule) {
        const blockStartMinutes = this.timeToMinutes(timeBlock.start)
        const blockEndMinutes = this.timeToMinutes(timeBlock.end)
        const originalMinutes = this.timeToMinutes(originalTimeString)

        // Check if original time fits within this block
        if (originalMinutes >= blockStartMinutes && originalMinutes < blockEndMinutes) {
          const newDateTime = new Date(targetDate)
          newDateTime.setHours(originalHour, originalMinute, 0, 0)
          return newDateTime
        }
      }

      // If original time doesn't fit, try to find the closest alternative
      let bestFit = null
      let minDistance = Infinity

      for (const timeBlock of daySchedule) {
        const blockStartMinutes = this.timeToMinutes(timeBlock.start)
        const blockEndMinutes = this.timeToMinutes(timeBlock.end)

        // Try different positions within this block (every 15 minutes)
        for (let startMinutes = blockStartMinutes; startMinutes < blockEndMinutes; startMinutes += 15) {
          const distance = Math.abs(startMinutes - this.timeToMinutes(originalTimeString))

          if (distance < minDistance) {
            minDistance = distance

            const hours = Math.floor(startMinutes / 60)
            const minutes = startMinutes % 60

            const newDateTime = new Date(targetDate)
            newDateTime.setHours(hours, minutes, 0, 0)

            bestFit = newDateTime
          }
        }
      }

      return bestFit
    },

    handleMouseUp() {
      if (this.draggingJob) {
        // If no lane change and no time change, treat as simple click and do nothing
        const originalStart = this.parseJobDate(this.draggingJob.originalStartDate || this.draggingJob.start_date)
        const originalEnd = this.parseJobDate(this.draggingJob.originalEndDate || this.draggingJob.end_date)
        const currentStart = this.parseJobDate(this.draggingJob.start_date)
        const currentEnd = this.parseJobDate(this.draggingJob.end_date)

        const sameLane = !this.targetLaneId || (this.draggingJob.originalLaneId && this.targetLaneId === this.draggingJob.originalLaneId)
        const sameTime = originalStart?.getTime?.() === currentStart?.getTime?.() && originalEnd?.getTime?.() === currentEnd?.getTime?.()

        if (sameLane && sameTime) {
          if (this.draggingJob.originalLaneId) {
            this.draggingJob.lane_id = this.draggingJob.originalLaneId
            delete this.draggingJob.originalLaneId
          }

          // Clean up drag state without triggering any API
          this.draggingJob = null
          this.dragStartTime = null
          this.targetLaneId = null
          this.targetPoolId = null
          return
        }

        // Check if we're moving to a different lane
        if (this.targetLaneId && this.draggingJob.originalLaneId && this.targetLaneId !== this.draggingJob.originalLaneId) {
          // Prepare data for confirmation dialog
          const fromLane = this.lanes.find(lane => lane.id === this.draggingJob.originalLaneId)
          let toLane = this.lanes.find(lane => lane.id === this.targetLaneId)
          const toPool = toLane.pool

          this.pendingMoveData = {
            jobId: this.draggingJob.job_id,
            jobTitle: this.draggingJob.job_title,
            jobDefinitionId: this.draggingJob.job_definition_id,
            job: this.draggingJob,
            fromLane,
            toLane,
            timeRange: `${this.formatJobTime(this.draggingJob.start_date)} - ${this.formatJobTime(this.draggingJob.end_date)}`,
            newStartDate: this.draggingJob.start_date,
            newEndDate: this.draggingJob.end_date,
            newLaneId: toLane?.lane_id || toLane?.id || null,
            originalLaneId: this.draggingJob.originalLaneId,
            originalStartDate: this.draggingJob.originalStartDate,
            originalEndDate: this.draggingJob.originalEndDate,
            originalStatusId: this.draggingJob.status_id,
            newStatusId: null,
            sumSLA: false,
            slaHours: null,
            selectedPoolId: toLane.pool.pool_id,
          }

          const canMoveToPool = this.checkCanMoveToPool(toPool.pool_id)

          if (false == canMoveToPool) {
            this.cancelLaneMove()

            this.draggingJob = null
            this.dragStartTime = null
            this.targetLaneId = null
            this.targetPoolId = null

            return
          }

          this.showMoveConfirmDialog = true
        } else {
          if (
            false == this.isWorkingTime(this.draggingJob.start_date)
            || false == this.isWorkingTime(this.draggingJob.end_date)
          ) {
            this.cancelLaneMove()

            this.draggingJob = null
            this.dragStartTime = null
            this.targetLaneId = null
            this.targetPoolId = null

            return
          }

          // Just time change, no lane change - check for overlaps
          if (this.draggingJob.originalLaneId) {
            this.draggingJob.lane_id = this.draggingJob.originalLaneId
            delete this.draggingJob.originalLaneId
          }

          // Check for overlaps in the same lane
          const overlappingJobs = this.checkForOverlaps(
            this.draggingJob.job_id,
            this.draggingJob.lane_id,
            this.parseJobDate(this.draggingJob.start_date),
            this.parseJobDate(this.draggingJob.end_date)
          )

          if (overlappingJobs.length > 0) {
            // Show overlap confirmation dialog
            const lane = this.lanes.find(lane => lane.id === this.draggingJob.lane_id)
            this.pendingOverlapData = {
              jobId: this.draggingJob.job_id,
              jobTitle: this.draggingJob.job_title,
              timeRange: `${this.formatJobTime(this.draggingJob.start_date)} - ${this.formatJobTime(this.draggingJob.end_date)}`,
              laneName: lane?.lane_title || 'Unknown Lane',
              overlappingJobs: overlappingJobs,
              originalMoveData: {
                jobId: this.draggingJob.job_id,
                newStartDate: this.draggingJob.start_date,
                newEndDate: this.draggingJob.end_date,
                newLaneId: this.draggingJob.lane_id,
                originalStartDate: this.draggingJob.originalStartDate,
                originalEndDate: this.draggingJob.originalEndDate,
                originalLaneId: this.draggingJob.originalLaneId
              }
            }

            this.showOverlapConfirmDialog = true
          } else {
            // No overlaps, proceed with the update
            this.pendingJobToUpdateTimeInLane = {
              jobId: this.draggingJob.job_id,
              startDate: this.draggingJob.start_date,
              endDate: this.draggingJob.end_date,
              originalStartDate: this.draggingJob.originalStartDate,
              originalEndDate: this.draggingJob.originalEndDate
            }

            this.showUpdateJobTimeInLaneDialog = true
          }
        }

        this.draggingJob = null
        this.dragStartTime = null
        this.targetLaneId = null
        this.targetPoolId = null
      }

      if (this.resizingJob) {
        // Check for overlaps after resize
        const overlappingJobs = this.checkForOverlaps(
          this.resizingJob.job_id,
          this.resizingJob.lane_id,
          this.parseJobDate(this.resizingJob.start_date),
          this.parseJobDate(this.resizingJob.end_date)
        )

        if (overlappingJobs.length > 0) {
          // Show overlap confirmation dialog
          const lane = this.lanes.find(lane => lane.id === this.resizingJob.lane_id)
          this.pendingOverlapData = {
            jobId: this.resizingJob.job_id,
            jobTitle: this.resizingJob.job_title,
            timeRange: `${this.formatJobTime(this.resizingJob.start_date)} - ${this.formatJobTime(this.resizingJob.end_date)}`,
            laneName: lane?.lane_title || 'Unknown Lane',
            overlappingJobs: overlappingJobs,
            originalMoveData: {
              jobId: this.resizingJob.job_id,
              newStartDate: this.resizingJob.start_date,
              newEndDate: this.resizingJob.end_date,
              newLaneId: this.resizingJob.lane_id,
              originalStartDate: this.resizingJob.originalStartDate,
              originalEndDate: this.resizingJob.originalEndDate,
              originalLaneId: this.resizingJob.lane_id
            }
          }

          this.showOverlapConfirmDialog = true
        } else {
          // No overlaps, proceed with the update
          this.pendingJobToUpdateTimeInLane = {
            jobId: this.resizingJob.job_id,
            direction: this.resizeDirection,
            startDate: this.resizingJob.start_date,
            endDate: this.resizingJob.end_date,
            originalStartDate: this.resizingJob.originalStartDate,
            originalEndDate: this.resizingJob.originalEndDate,
          }

          this.showUpdateJobTimeInLaneDialog = true
        }

        this.resizingJob = null
        this.resizeDirection = null
      }
    },

    getAllPools() {
      return (this.pools || []).map(p => ({ pool_id: p.pool_id, pool_title: p.pool_title, raw: p }))
    },

    checkCanMoveToPool(poolId) {
      const MOVE_ACL = 1

      const moveAcl = this.userAcls.find(acl => acl.pool_id == poolId && acl.acl & MOVE_ACL)

      return !!moveAcl
    },

    cancelLaneMove() {
      if (this.pendingMoveData) {
        // Revert the job to its original lane
        const originalJob = this.filteredJobs.find(job => job.job_id === this.pendingMoveData.jobId)
        if (originalJob && this.pendingMoveData.originalLaneId) {
          originalJob.lane_id = this.pendingMoveData.originalLaneId
          delete originalJob.originalLaneId
        }

        // Revert to original times if they exist
        if (this.pendingMoveData.originalStartDate && this.pendingMoveData.originalStartDate !== originalJob.start_date) {
          originalJob.start_date = this.pendingMoveData.originalStartDate
        }

        if (this.pendingMoveData.originalEndDate && this.pendingMoveData.originalEndDate !== originalJob.end_date) {
          originalJob.end_date = this.pendingMoveData.originalEndDate
        }

        this[JobMutations.UPDATE_JOB](originalJob)

        this.showMoveConfirmDialog = false
        this.pendingMoveData = null
        this.$forceUpdate()
      }
    },

    // Lane move confirmation methods
    async confirmLaneMove() {
      if (this.pendingMoveData) {
        // Check for overlaps before confirming the move
        const overlappingJobs = this.checkForOverlaps(
          this.pendingMoveData.jobId,
          this.pendingMoveData.newLaneId,
          this.parseJobDate(this.pendingMoveData.newStartDate),
          this.parseJobDate(this.pendingMoveData.newEndDate)
        )

        if (
          overlappingJobs.length > 0
          && this.pendingMoveData.fromLane.lane_id == this.pendingMoveData.newLaneId
        ) {
          // Show overlap confirmation dialog
          const lane = this.lanes.find(lane => lane.id === this.pendingMoveData.newLaneId)
          this.pendingOverlapData = {
            jobId: this.pendingMoveData.jobId,
            jobTitle: this.pendingMoveData.jobTitle,
            timeRange: this.pendingMoveData.timeRange,
            laneName: lane?.lane_title || 'Unknown Lane',
            overlappingJobs: overlappingJobs,
            originalMoveData: {
              ...this.pendingMoveData,
              originalStartDate: this.pendingMoveData.originalStartDate || this.filteredJobs.find(j => j.job_id === this.pendingMoveData.jobId)?.start_date,
              originalEndDate: this.pendingMoveData.originalEndDate || this.filteredJobs.find(j => j.job_id === this.pendingMoveData.jobId)?.end_date,
              originalLaneId: this.pendingMoveData.originalLaneId
            }
          }

          this.showMoveConfirmDialog = false
          this.showOverlapConfirmDialog = true
        } else {
          // No overlaps, proceed with the move
          this.$emit('update-job-time', this.pendingMoveData.jobId, {
            start_date: this.pendingMoveData.newStartDate,
            end_date: this.pendingMoveData.newEndDate
          })

          this.$emit('move-job', this.pendingMoveData.jobId, this.pendingMoveData.newLaneId)

          const newLane = this.lanes.find(l => l.lane_id == this.pendingMoveData.newLaneId)

          const params = {
            job_id: this.pendingMoveData.jobId,
            pool_id: newLane.pool.pool_id,
            lane_id: this.pendingMoveData.newLaneId,
            status_id: this.pendingMoveData.newStatusId,
            sum_sla: this.pendingMoveData.sumSLA || false,
            ...(Number(this.pendingMoveData.slaHours) > 0 ? { sla: Number(this.pendingMoveData.slaHours) } : {}),
            pool_title: newLane.pool.pool_title
          }

          // Snapshot data needed for notification before we clear pendingMoveData
          let moveContext = {
            jobTitle: this.pendingMoveData?.jobTitle,
            startDate: this.pendingMoveData?.newStartDate,
            laneUserGroupId: newLane?.user_group_id,
          }

          if (this.pendingMoveData.job?.parent_job_id) {
            moveContext.jobTitle = `${this.pendingMoveData?.jobTitle} - ${this.pendingMoveData?.job?.fields?.parent_job_title}`
          }

          // Check if status changed and create comment
          if (this.pendingMoveData.originalStatusId !== this.pendingMoveData.newStatusId) {
            try {
              const newStatus = this.statuses.find(s => s.status_id === this.pendingMoveData.newStatusId)
              const newStatusTitle = newStatus?.status_title || 'Unknown'

              const commentData = {
                job_id: parseInt(this.pendingMoveData.jobId),
                message: `O <b>status</b> alterado para <b>${newStatusTitle}</b>`,
                comment_type: 2
              }

              await CommentAPI.createComment(commentData)
            } catch (error) {
              console.error('Error creating status change comment:', error)
            }
          }

          this.loading = true
          const movedJob = this.pendingMoveData.job
          this[JobActions.MOVE_TO_POOL](params)
            .then((result) => {
              this.removeSubtasksForTerminalParent(movedJob, params.status_id)
              this.notifyJobEnteredLane(result, moveContext)

              this.$nextTick(() => {
                this.$forceUpdate()
              })
            })
            .finally(() => {
              this.loading = false
            })

          this.showMoveConfirmDialog = false
          this.pendingMoveData = null
        }
      }
    },

    handleMoveDialogConfirm(payload) {
      if (!this.pendingMoveData) return
      if (payload.poolId && payload.poolId !== this.pendingMoveData.selectedPoolId) {
        this.pendingMoveData.selectedPoolId = payload.poolId
      }
      this.pendingMoveData.newStatusId = payload.statusId
      this.pendingMoveData.newLaneId = payload.laneId
      this.pendingMoveData.sumSLA = !!payload.sumSLA
      this.pendingMoveData.slaHours = payload.slaHours

      const overlappingJobs = this.checkForOverlaps(
        this.pendingMoveData.jobId,
        this.pendingMoveData.newLaneId,
        this.parseJobDate(this.pendingMoveData.newStartDate),
        this.parseJobDate(this.pendingMoveData.newEndDate)
      )

      if (
        overlappingJobs.length > 0
        && this.pendingMoveData.fromLane.lane_id == this.pendingMoveData.newLaneId
      ) {
        const lane = this.lanes.find(lane => lane.id === this.pendingMoveData.newLaneId)
        this.pendingOverlapData = {
          jobId: this.pendingMoveData.jobId,
          jobTitle: this.pendingMoveData.jobTitle,
          timeRange: this.pendingMoveData.timeRange,
          laneName: lane?.lane_title || 'Unknown Lane',
          overlappingJobs: overlappingJobs,
          originalMoveData: {
            ...this.pendingMoveData,
            originalStartDate: this.pendingMoveData.originalStartDate || this.filteredJobs.find(j => j.job_id === this.pendingMoveData.jobId)?.start_date,
            originalEndDate: this.pendingMoveData.originalEndDate || this.filteredJobs.find(j => j.job_id === this.pendingMoveData.jobId)?.end_date,
            originalLaneId: this.pendingMoveData.originalLaneId
          }
        }

        this.showMoveConfirmDialog = false
        this.showOverlapConfirmDialog = true
      } else {
        this.$emit('update-job-time', this.pendingMoveData.jobId, {
          start_date: this.pendingMoveData.newStartDate,
          end_date: this.pendingMoveData.newEndDate
        })

        this.$emit('move-job', this.pendingMoveData.jobId, this.pendingMoveData.newLaneId)

        const newLane = this.lanes.find(l => l.lane_id == this.pendingMoveData.newLaneId)

        const params = {
          job_id: this.pendingMoveData.jobId,
          pool_id: newLane.pool.pool_id,
          lane_id: this.pendingMoveData.newLaneId,
          status_id: this.pendingMoveData.newStatusId,
          sum_sla: this.pendingMoveData.sumSLA || false,
          ...(Number(this.pendingMoveData.slaHours) > 0 ? { sla: Number(this.pendingMoveData.slaHours) } : {}),
          pool_title: newLane.pool.pool_title
        }

        let moveContext = {
          jobTitle: this.pendingMoveData?.jobTitle,
          startDate: this.pendingMoveData?.newStartDate,
          laneUserGroupId: newLane?.user_group_id,
          poolId: newLane.pool.pool_id,
        }

        if (this.pendingMoveData.job?.parent_job_id) {
          moveContext.jobTitle = `${this.pendingMoveData.jobTitle} - ${this.pendingMoveData?.job?.fields?.parent_job_title}`
        }

        this.loading = true
        const movedJob = this.pendingMoveData.job
        this[JobActions.MOVE_TO_POOL](params)
          .then((result) => {
            this.removeSubtasksForTerminalParent(movedJob, params.status_id)
            this.notifyJobEnteredLane(result, moveContext)
            // Force re-evaluation of canInteractWithJob for all job cards
            this.$nextTick(() => {
              this.$forceUpdate()
            })
          })
          .finally(() => {
            this.loading = false
          })

        this.showMoveConfirmDialog = false
        this.pendingMoveData = null
      }
    },

    // Build and send notification when a job enters a lane
    async notifyJobEnteredLane(result, context = {}) {
      try {
        const recipientsResult = await JobAPI.getCustomerVisibleActors(
          result.job_id,
          result.lane_id
        )
        const usersIds = recipientsResult.data?.users_ids ?? []
        const groupsIds = recipientsResult.data?.user_group_ids ?? []

        const jobTitle = context.jobTitle || (this.pendingMoveData && this.pendingMoveData.jobTitle) || ''
        const startDateStr = (result && result.start_date) || (this.pendingMoveData && this.pendingMoveData.newStartDate)
        const link = this.buildAgendaLinks(context.startDate || startDateStr)

        const subject = this.$t('job_entered_lane_subject', { jobTitle })
        const content = this.$t('job_entered_lane_content', { jobTitle, link })

        const options = {
          subject,
          content,
          users_ids: usersIds,
          groups_ids: groupsIds,
        }

        this[NotificationActions.SEND_MESSAGE](options)
      } catch (e) {
        console.error(e)
      }
    },

    // Build agenda link with 3-day interval and a given start date
    buildAgendaLinks(dateStr) {
      const appId = this.$route.params.appId
      const contentAppId = this.$route.params.contentAppId || 1
      const route = this.$router.resolve({ name: 'JobHomeContent', params: { appId, contentAppId } })

      const url = new URL(route.href, window.location.origin)
      url.searchParams.set('timelineInterval', '2') // 3 dias
      const dateParam = this.formatDateParamForURL(dateStr)
      if (dateParam) url.searchParams.set('date', dateParam)
      return url.href
    },

    // Convert various date formats to 'YYYY/MM/DD-HH:mm:ss'
    formatDateParamForURL(dateInput) {
      try {
        let d
        if (dateInput instanceof Date) {
          d = dateInput
        } else if (typeof dateInput === 'string') {
          // Try parse using existing parser
          d = this.parseJobDate(dateInput)
        } else {
          d = new Date()
        }
        if (isNaN(d)) return null

        const pad = (n) => (n < 10 ? '0' + n : '' + n)
        const yyyy = d.getFullYear()
        const mm = pad(d.getMonth() + 1)
        const dd = pad(d.getDate())
        const HH = pad(d.getHours())
        const MM = pad(d.getMinutes())
        const SS = pad(d.getSeconds())
        return `${yyyy}/${mm}/${dd}-${HH}:${MM}:${SS}`
      } catch (_) { return null }
    },

    // Create subtask methods
    openCreateSubtaskDialog(job) {
      if (job.readonly) return

      this.createSubtaskJobContext = job
      this.showCreateSubtaskDialog = true
    },

    async handleCreateSubtaskConfirm(payload) {
      if (!this.createSubtaskJobContext || this.createSubtaskJobContext.readonly) return

      try {
        this.loading = true

        const params = {
          job_id: this.createSubtaskJobContext.job_id,
          name: payload.name,
          pool_id: payload.poolId,
          lane_id: payload.laneId,
          status_id: payload.statusId,
        }

        await this[JobActions.CREATE_SUBTASK](params)

        this.showCreateSubtaskDialog = false
        this.createSubtaskJobContext = null

        // Refresh jobs list
        await this.fetchJobs(this.timeColumns)
      } catch (error) {
        console.error('Error creating subtask:', error)
      } finally {
        this.loading = false
      }
    },

    // Send job methods
    openSendJobDialog(job) {
      // Prepare data for the move dialog, similar to drag behavior
      const fromLane = this.lanes.find(lane => lane.lane_id === job.lane_id)

      this.pendingMoveData = {
        jobId: job.job_id,
        jobTitle: job.job_title,
        jobDefinitionId: job.job_definition_id,
        job,
        fromLane,
        toLane: fromLane, // Initially set to same lane
        timeRange: `${this.formatJobTime(job.start_date)} - ${this.formatJobTime(job.end_date)}`,
        newStartDate: job.start_date,
        newEndDate: job.end_date,
        newLaneId: job.lane_id,
        originalLaneId: job.lane_id,
        originalStartDate: job.start_date,
        originalEndDate: job.end_date,
        originalStatusId: job.status_id,
        newStatusId: job.status_id,
        sumSLA: false,
        slaHours: null,
        selectedPoolId: fromLane?.pool?.pool_id || null,
      }

      this.showMoveConfirmDialog = true
    },

    // Finish subtask methods
    openFinishSubtaskDialog(job) {
      this.finishSubtaskJobContext = job
      this.showFinishSubtaskDialog = true
    },

    async confirmFinishSubtask() {
      if (!this.finishSubtaskJobContext) return

      try {
        this.loading = true

        const params = {
          name: this.finishSubtaskJobContext.job_title
        }

        await this[JobActions.FINISH_SUBTASK]({
          jobId: this.finishSubtaskJobContext.job_id,
          params
        })

        this.showFinishSubtaskDialog = false
        this.finishSubtaskJobContext = null

        // Refresh jobs list
        await this.fetchJobs(this.timeColumns)
      } catch (error) {
        console.error('Error finishing subtask:', error)
      } finally {
        this.loading = false
      }
    },

    cancelFinishSubtask() {
      this.showFinishSubtaskDialog = false
      this.finishSubtaskJobContext = null
    },

    // Finish job methods
    openFinishJobDialog(job) {
      this.finishJobJobContext = job
      this.showFinishJobDialog = true
    },

    async confirmFinishJob() {
      if (!this.finishJobJobContext) return

      try {
        this.loading = true

        const params = {
          name: this.finishJobJobContext.job_title
        }

        await this[JobActions.FINISH_JOB]({
          jobId: this.finishJobJobContext.job_id,
          params
        })

        this.showFinishJobDialog = false
        this.finishJobJobContext = null

        // The store has already been updated by FINISH_JOB. Avoid querying the
        // index immediately, because it may still return the just-closed subtasks.
        this.$nextTick(() => this.$forceUpdate())
      } catch (error) {
        console.error('Error finishing job:', error)
      } finally {
        this.loading = false
      }
    },

    cancelFinishJob() {
      this.showFinishJobDialog = false
      this.finishJobJobContext = null
    },

    async handleReopenJob(job) {
      if (!job?.readonly || job.parent_job_id || !this.filterShowFinished) return

      try {
        this.loading = true
        await this[JobActions.REOPEN_JOB](job.job_id)
        this[JobMutations.REMOVE_JOB](job.job_id)
      } catch (error) {
        console.error('Error reopening job:', error)
      } finally {
        this.loading = false
      }
    },

    // Overlap detection and confirmation methods
    checkForOverlaps(jobId, laneId, startDateTime, endDateTime) {
      // Pega todos os jobs sem levar em consideracao os filtros da tela
      const laneJobs = this.jobs.filter(job => job.lane_id === laneId)

      const overlaps = laneJobs.filter(job => {
        if (job.job_id === jobId) return false // Don't check against itself

        const jobStart = this.parseJobDate(job.start_date)
        const jobEnd = this.parseJobDate(job.end_date)

        // Check if there's any time overlap
        const hasOverlap = (startDateTime < jobEnd && endDateTime > jobStart)

        return hasOverlap
      })

      return overlaps
    },

    confirmUpdateJobTimeInLane() {
      if (this.pendingJobToUpdateTimeInLane === null) return

      const moveData = this.pendingJobToUpdateTimeInLane
      this.loading = true

      this.$emit('update-job-time', moveData.jobId, {
        start_date: moveData.startDate,
        end_date: moveData.endDate
      })

      const params = {
        job_id: moveData.jobId,
        start_date: format(this.parseJobDate(moveData.startDate), 'yyyy-MM-dd HH:mm:ss', { locale: this.currentLocale }),
        end_date: format(this.parseJobDate(moveData.endDate), 'yyyy-MM-dd HH:mm:ss', { locale: this.currentLocale }),
      }

      this[JobActions.CHANGE_SLOT](params)
        .finally(() => {
          this.loading = false
          this.showUpdateJobTimeInLaneDialog = false
          this.pendingJobToUpdateTimeInLane = null
        })
    },

    cancelUpdateJobTimeInLane() {
      if (this.pendingJobToUpdateTimeInLane === null) return

      const moveData = this.pendingJobToUpdateTimeInLane
      const originalJob = this.filteredJobs.find(job => job.job_id === moveData.jobId)

      if(originalJob && moveData.originalStartDate) {
        originalJob.start_date = moveData.originalStartDate
      }

      if (originalJob && moveData.originalEndDate) {
        originalJob.end_date = moveData.originalEndDate
      }

      this[JobMutations.UPDATE_JOB](originalJob)

      this.showUpdateJobTimeInLaneDialog = false
      this.pendingJobToUpdateTimeInLane = null
      this.draggingJob = null
      this.resizingJob = null
      this.resizeDirection = null
      this.$forceUpdate()
    },

    confirmOverlap() {
      if (this.pendingOverlapData && this.pendingOverlapData.originalMoveData) {
        const moveData = this.pendingOverlapData.originalMoveData

        // Proceed with the move despite overlap
        this.$emit('update-job-time', moveData.jobId, {
          start_date: moveData.newStartDate,
          end_date: moveData.newEndDate
        })

        const params = {
          job_id: moveData.jobId,
          start_date: format(this.parseJobDate(moveData.newStartDate), 'yyyy-MM-dd HH:mm:ss'),
          end_date: format(this.parseJobDate(moveData.newEndDate), 'yyyy-MM-dd HH:mm:ss'),
        }

        this.loading = true
        this[JobActions.CHANGE_SLOT](params)
          .finally(() => {
            this.loading = false
          })

        this.$emit('move-job', moveData.jobId, moveData.newLaneId)

        this.showOverlapConfirmDialog = false
        this.pendingOverlapData = null
        this.pendingMoveData = null
      }
    },

    cancelOverlap() {
      if (this.pendingOverlapData && this.pendingOverlapData.originalMoveData) {
        // Revert the job to its original position
        const moveData = this.pendingOverlapData.originalMoveData
        const originalJob = this.filteredJobs.find(job => job.job_id === moveData.jobId)

        if (originalJob) {
          // Revert to original times and lane
          if (moveData.originalLaneId && moveData.originalLaneId !== originalJob.lane_id) {
            originalJob.lane_id = moveData.originalLaneId
          }

          // Revert to original times if they exist
          if (moveData.originalStartDate && moveData.originalStartDate !== originalJob.start_date) {
            originalJob.start_date = moveData.originalStartDate
          }
          if (moveData.originalEndDate && moveData.originalEndDate !== originalJob.end_date) {
            originalJob.end_date = moveData.originalEndDate
          }

          // Clean up any temporary properties
          if (originalJob.originalStartDate) {
            delete originalJob.originalStartDate
          }
          if (originalJob.originalEndDate) {
            delete originalJob.originalEndDate
          }
          if (originalJob.originalLaneId) {
            delete originalJob.originalLaneId
          }

          // Update the job in the store
          this[JobMutations.UPDATE_JOB](originalJob)
        }

        this.showOverlapConfirmDialog = false
        this.pendingOverlapData = null
        this.pendingMoveData = null
        this.$forceUpdate()
      }
    },

    // Timeline generation methods
    generateHourColumns() {
      const now = this.timelineTime
      // Round current time to the nearest full hour
      const currentHour = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), 0, 0, 0)
      const halfColumns = Math.floor(this.columnsCount / 2)

      // Generate all working hours in a wide range to ensure we have enough
      const allWorkingHours = []
      const daysToCheck = 30 // Check 30 days before and after

      // Check if current hour is a working hour (only for today)
      const todayName = format(now, 'EEEE')
      const todaySchedule = this.workingSchedule[todayName]
      let isCurrentHourWorking = false

      if (todaySchedule && todaySchedule.length > 0) {
        const currentTimeString = format(now, 'HH:mm')
        isCurrentHourWorking = todaySchedule.some(timeBlock => {
          return currentTimeString >= timeBlock.start && currentTimeString < timeBlock.end
        })
      }

      for (let dayOffset = -daysToCheck; dayOffset <= daysToCheck; dayOffset++) {
        const checkDate = new Date(currentHour)
        checkDate.setDate(checkDate.getDate() + dayOffset)

        const dayName = format(checkDate, 'EEEE')
        const daySchedule = this.workingSchedule[dayName]

        if (daySchedule && daySchedule.length > 0) {
          daySchedule.forEach(timeBlock => {
            const startHour = parseInt(timeBlock.start.split(':')[0])
            const endHour = parseInt(timeBlock.end.split(':')[0])

            for (let hour = startHour; hour < endHour; hour++) {
              const workingHour = new Date(checkDate)
              workingHour.setHours(hour, 0, 0, 0)

              // Only mark as current if it's the current hour AND it's a working hour
              const isCurrent = workingHour.getTime() === currentHour.getTime() && isCurrentHourWorking

              allWorkingHours.push({
                time: workingHour,
                dateLabel: format(workingHour, 'MMM dd', { locale: this.currentLocale }),
                timeLabel: format(workingHour, 'HH:mm', { locale: this.currentLocale }),
                isCurrent: isCurrent,
                isWorkingTime: true
              })
            }
          })
        }
      }

      // Sort by time
      allWorkingHours.sort((a, b) => a.time - b.time)

      // Find current hour index
      let currentIndex = allWorkingHours.findIndex(col => col.isCurrent)

      if (currentIndex === -1) {
        // If current hour is not visible (not a working hour), center around current time
        currentIndex = allWorkingHours.findIndex(col => col.time >= currentHour)
        if (currentIndex === -1) {
          // If no future working hours, use the last available
          currentIndex = allWorkingHours.length - 1
        }
      }

      // Calculate start and end indices to center current time
      const startIndex = Math.max(0, currentIndex - halfColumns)
      const endIndex = Math.min(allWorkingHours.length, startIndex + this.columnsCount)

      // If we don't have enough columns, adjust the start index
      const actualStartIndex = Math.max(0, endIndex - this.columnsCount)

      return allWorkingHours.slice(actualStartIndex, endIndex)
    },

    generateDayColumns() {
      const now = this.timelineTime
      // Round current time to the nearest full day
      const currentDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0)
      const halfColumns = Math.floor(this.columnsCount / 2)

      // Generate all working days in a wide range
      const allWorkingDays = []
      const daysToCheck = 60 // Check 60 days before and after

      for (let dayOffset = -daysToCheck; dayOffset <= daysToCheck; dayOffset++) {
        const checkDate = new Date(currentDay)
        checkDate.setDate(checkDate.getDate() + dayOffset)

        const dayName = format(checkDate, 'EEEE')
        const daySchedule = this.workingSchedule[dayName]

        // Only include days that have working hours
        if (daySchedule && daySchedule.length > 0) {
          allWorkingDays.push({
            time: checkDate,
            dateLabel: format(checkDate, 'MMM dd', { locale: this.currentLocale }),
            timeLabel: format(checkDate, 'EEE', { locale: this.currentLocale }),
            isCurrent: checkDate.getTime() === currentDay.getTime(),
            isWorkingTime: true
          })
        }
      }

      // Sort by time
      allWorkingDays.sort((a, b) => a.time - b.time)

      // Find current day index
      let currentIndex = allWorkingDays.findIndex(col => col.isCurrent)

      if (currentIndex === -1) {
        // If current day is not in working days, find the closest working day
        currentIndex = allWorkingDays.findIndex(col => col.time >= currentDay)
        if (currentIndex === -1) {
          // If no future working days, use the last available
          currentIndex = allWorkingDays.length - 1
        }
      }

      // Calculate start and end indices to center current time
      const startIndex = Math.max(0, currentIndex - halfColumns)
      const endIndex = Math.min(allWorkingDays.length, startIndex + this.columnsCount)

      // If we don't have enough columns, adjust the start index
      const actualStartIndex = Math.max(0, endIndex - this.columnsCount)

      return allWorkingDays.slice(actualStartIndex, endIndex)
    },

    // Current time position calculation methods
    calculateHourTimePosition(now, headerWidth) {
      const todayName = format(now, 'EEEE')
      const todaySchedule = this.workingSchedule[todayName]
      const currentTimeString = format(now, 'HH:mm')

      // Check if current time is within working hours
      let isCurrentTimeWorking = false
      let nearestEdgeTime = null
      let nearestEdgeDate = null

      if (todaySchedule && todaySchedule.length > 0) {
        // Check if current time is within any working period
        for (const timeBlock of todaySchedule) {
          if (currentTimeString >= timeBlock.start && currentTimeString < timeBlock.end) {
            isCurrentTimeWorking = true
            break
          }
        }

        // If not in working hours, find the nearest edge from today's schedule
        if (!isCurrentTimeWorking) {
          let closestDistance = Infinity

          for (const timeBlock of todaySchedule) {
            const startMinutes = this.timeToMinutes(timeBlock.start)
            const endMinutes = this.timeToMinutes(timeBlock.end)
            const currentMinutes = this.timeToMinutes(currentTimeString)

            // Check distance to start of period
            const distanceToStart = Math.abs(currentMinutes - startMinutes)
            if (distanceToStart < closestDistance) {
              closestDistance = distanceToStart
              nearestEdgeTime = timeBlock.start
              nearestEdgeDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
            }

            // Check distance to end of period
            const distanceToEnd = Math.abs(currentMinutes - endMinutes)
            if (distanceToEnd < closestDistance) {
              closestDistance = distanceToEnd
              nearestEdgeTime = timeBlock.end
              nearestEdgeDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
            }
          }
        }
      }

      if (isCurrentTimeWorking) {
        // Find the current time column in the timeline
        const currentHour = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), 0, 0, 0)
        const currentIndex = this.timeColumns.findIndex(col => col.time.getTime() === currentHour.getTime())

        if (currentIndex === -1) {
          return -1 // Hide indicator if current time is not visible
        }

        // Calculate exact position within the hour based on minutes
        const minutes = now.getMinutes()
        const minuteOffset = (minutes / 60) * this.columnWidth

        return headerWidth + (currentIndex * this.columnWidth) + minuteOffset
      } else if (nearestEdgeTime && nearestEdgeDate) {
        // Create the exact datetime for the nearest edge
        const edgeHour = parseInt(nearestEdgeTime.split(':')[0])
        const edgeMinutes = parseInt(nearestEdgeTime.split(':')[1])
        const edgeDateTime = new Date(nearestEdgeDate)
        edgeDateTime.setHours(edgeHour, edgeMinutes, 0, 0)

        // Find the column that contains this edge time
        const edgeColumnIndex = this.timeColumns.findIndex(col => {
          const colTime = col.time.getTime()
          const edgeTime = edgeDateTime.getTime()
          // Check if the edge time falls within this hour column
          return edgeTime >= colTime && edgeTime < colTime + (60 * 60 * 1000)
        })

        if (edgeColumnIndex !== -1) {
          const minuteOffset = (edgeMinutes / 60) * this.columnWidth
          return headerWidth + (edgeColumnIndex * this.columnWidth) + minuteOffset
        }

        // If exact column not found, try to find the closest visible column
        let closestColumnIndex = -1
        let closestDistance = Infinity

        this.timeColumns.forEach((col, index) => {
          const distance = Math.abs(col.time.getTime() - edgeDateTime.getTime())
          if (distance < closestDistance) {
            closestDistance = distance
            closestColumnIndex = index
          }
        })

        if (closestColumnIndex !== -1) {
          // Position at the edge of the closest column
          const isBeforeColumn = edgeDateTime.getTime() < this.timeColumns[closestColumnIndex].time.getTime()
          if (isBeforeColumn) {
            // Position at the start of the column
            return headerWidth + (closestColumnIndex * this.columnWidth)
          } else {
            // Position at the end of the column
            return headerWidth + ((closestColumnIndex + 1) * this.columnWidth)
          }
        }
      }

      return -1 // Hide indicator if no suitable position found
    },

    calculateDayTimePosition(now, headerWidth) {
      const currentDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0)
      const currentIndex = this.timeColumns.findIndex(col => col.time.getTime() === currentDay.getTime())

      if (currentIndex === -1) {
        return -1 // Hide indicator if current day is not visible
      }

      // For days view, position at the center of the day column
      return headerWidth + (currentIndex * this.columnWidth) + (this.columnWidth / 2)
    },

    // Column width calculation
    calculateColumnWidth() {
      this.$nextTick(() => {
        const container = this.$refs.timelineContainer
        if (!container) return

        const containerWidth = container.clientWidth
        const headerWidth = 198 // Job labels column width
        const borderWidth = 2 // Account for the header border
        const availableWidth = containerWidth - headerWidth - borderWidth

        // Calculate column width to fit all columns without horizontal scroll
        const actualColumnsCount = this.timeColumns.length || this.columnsCount

        if (actualColumnsCount > 0) {
          // Use the full available width by dividing exactly, ensuring we use every pixel
          this.columnWidth = Math.max(30, availableWidth / actualColumnsCount)
        } else {
          this.columnWidth = 60 // fallback
        }

        this.containerWidth = containerWidth
      })
    },

    // Improved drag and drop functionality to prevent glitching
    constrainJobToWorkingHours(newStartDateTime, duration) {
      const dayName = format(newStartDateTime, 'EEEE')
      const daySchedule = this.workingSchedule[dayName]

      if (!daySchedule || daySchedule.length === 0) {
        // If no working hours for this day, find the next working day
        return this.findNextWorkingTime(newStartDateTime, duration)
      }

      const timeString = format(newStartDateTime, 'HH:mm')
      const endDateTime = new Date(newStartDateTime.getTime() + duration)
      const endTimeString = format(endDateTime, 'HH:mm')

      // Check if the job fits within any working time block
      for (const timeBlock of daySchedule) {
        if (timeString >= timeBlock.start && endTimeString <= timeBlock.end) {
          // Job fits within this time block
          return {
            start_date: newStartDateTime.toISOString(),
            end_date: endDateTime.toISOString()
          }
        }
      }

      // Job doesn't fit, try to snap to the nearest working time block
      let bestFit = null
      let minDistance = Infinity

      for (const timeBlock of daySchedule) {
        const blockStartMinutes = this.timeToMinutes(timeBlock.start)
        const blockEndMinutes = this.timeToMinutes(timeBlock.end)
        const jobDurationMinutes = duration / (1000 * 60)

        // Try to fit at the start of the block
        if (jobDurationMinutes <= (blockEndMinutes - blockStartMinutes)) {
          const blockStartTime = new Date(newStartDateTime)
          const [hours, minutes] = timeBlock.start.split(':').map(Number)
          blockStartTime.setHours(hours, minutes, 0, 0)

          const distance = Math.abs(blockStartTime.getTime() - newStartDateTime.getTime())
          if (distance < minDistance) {
            minDistance = distance
            bestFit = {
              start_date: blockStartTime.toISOString(),
              end_date: new Date(blockStartTime.getTime() + duration).toISOString()
            }
          }
        }
      }

      return bestFit || this.findNextWorkingTime(newStartDateTime, duration)
    },

    findNextWorkingTime(startDateTime, duration) {
      // Find the next available working time slot
      const maxDaysToCheck = 7

      for (let dayOffset = 0; dayOffset < maxDaysToCheck; dayOffset++) {
        const checkDate = new Date(startDateTime)
        checkDate.setDate(checkDate.getDate() + dayOffset)

        const dayName = format(checkDate, 'EEEE')
        const daySchedule = this.workingSchedule[dayName]

        if (daySchedule && daySchedule.length > 0) {
          const jobDurationMinutes = duration / (1000 * 60)

          for (const timeBlock of daySchedule) {
            const blockStartMinutes = this.timeToMinutes(timeBlock.start)
            const blockEndMinutes = this.timeToMinutes(timeBlock.end)

            if (jobDurationMinutes <= (blockEndMinutes - blockStartMinutes)) {
              const [hours, minutes] = timeBlock.start.split(':').map(Number)
              const workingStartTime = new Date(checkDate)
              workingStartTime.setHours(hours, minutes, 0, 0)

              return {
                start_date: workingStartTime.toISOString(),
                end_date: new Date(workingStartTime.getTime() + duration).toISOString()
              }
            }
          }
        }
      }

      // Fallback: return original times if no working time found
      return {
        start_date: startDateTime.toISOString(),
        end_date: new Date(startDateTime.getTime() + duration).toISOString()
      }
    },

    // Weekend divider detection
    isWeekendDivider(column, index) {
      if (this.timelineViewMode === 'days') {
        const dayName = format(column.time, 'EEEE')
        return dayName === 'Friday' && index < this.timeColumns.length - 1
      } else {
        // For hours view, check if this is the last hour of Friday
        const dayName = format(column.time, 'EEEE')
        const nextColumn = this.timeColumns[index + 1]

        if (nextColumn) {
          const nextDayName = format(nextColumn.time, 'EEEE')
          return dayName === 'Friday' && nextDayName === 'Monday'
        }

        return false
      }
    },

    // Day divider detection (for different days)
    isDayDivider(column, index) {
      if (this.timelineViewMode !== 'hours' || index >= this.timeColumns.length - 1) {
        return false
      }

      const currentTime = column.time
      const nextColumn = this.timeColumns[index + 1]
      const nextTime = nextColumn.time

      // Check if the next column is on a different day
      const currentDay = format(currentTime, 'yyyy-MM-dd')
      const nextDay = format(nextTime, 'yyyy-MM-dd')

      return currentDay !== nextDay
    },

    // Time gap divider detection (for non-consecutive hours like 12:00 to 15:00)
    isTimeGapDivider(column, index) {
      if (this.timelineViewMode !== 'hours' || index >= this.timeColumns.length - 1) {
        return false
      }

      const currentTime = column.time
      const nextColumn = this.timeColumns[index + 1]
      const nextTime = nextColumn.time

      // Check if there's a gap of more than 1 hour between consecutive columns
      const timeDifference = nextTime.getTime() - currentTime.getTime()
      const oneHour = 60 * 60 * 1000

      return timeDifference > oneHour
    },

    // Working hour navigation helper methods
    findPreviousWorkingHour(currentTime) {
      const maxDaysToCheck = 30
      let checkTime = new Date(currentTime)

      // Start by going back one hour
      checkTime.setHours(checkTime.getHours() - 1)

      for (let dayOffset = 0; dayOffset < maxDaysToCheck; dayOffset++) {
        const checkDate = new Date(checkTime)
        checkDate.setDate(checkDate.getDate() - dayOffset)

        const dayName = format(checkDate, 'EEEE')
        const daySchedule = this.workingSchedule[dayName]

        if (daySchedule && daySchedule.length > 0) {
          // Check each time block for this day (in reverse order to find the latest working hour)
          for (let i = daySchedule.length - 1; i >= 0; i--) {
            const timeBlock = daySchedule[i]
            const startHour = parseInt(timeBlock.start.split(':')[0])
            const endHour = parseInt(timeBlock.end.split(':')[0])

            // Check each hour in this time block (in reverse order)
            for (let hour = endHour - 1; hour >= startHour; hour--) {
              const workingHour = new Date(checkDate)
              workingHour.setHours(hour, 0, 0, 0)

              // If this working hour is before our current check time, return it
              if (workingHour < checkTime) {
                return workingHour
              }
            }
          }
        }

        // If we didn't find a working hour on this day, move to the previous day
        // and set checkTime to end of that day for the next iteration
        if (dayOffset === 0) {
          checkTime = new Date(checkDate)
          checkTime.setHours(23, 59, 59, 999)
        }
      }

      // Fallback: return the original time minus one hour
      const fallback = new Date(currentTime)
      fallback.setHours(fallback.getHours() - 1)
      return fallback
    },

    findNextWorkingHour(currentTime) {
      const maxDaysToCheck = 30
      let checkTime = new Date(currentTime)

      // Start by going forward one hour
      checkTime.setHours(checkTime.getHours() + 1)

      for (let dayOffset = 0; dayOffset < maxDaysToCheck; dayOffset++) {
        const checkDate = new Date(checkTime)
        checkDate.setDate(checkDate.getDate() + dayOffset)

        const dayName = format(checkDate, 'EEEE')
        const daySchedule = this.workingSchedule[dayName]

        if (daySchedule && daySchedule.length > 0) {
          // Check each time block for this day
          for (const timeBlock of daySchedule) {
            const startHour = parseInt(timeBlock.start.split(':')[0])
            const endHour = parseInt(timeBlock.end.split(':')[0])

            // Check each hour in this time block
            for (let hour = startHour; hour < endHour; hour++) {
              const workingHour = new Date(checkDate)
              workingHour.setHours(hour, 0, 0, 0)

              // If this working hour is after our current check time, return it
              if (workingHour > checkTime) {
                return workingHour
              }
            }
          }
        }

        // If we didn't find a working hour on this day, move to the next day
        // and set checkTime to start of that day for the next iteration
        if (dayOffset === 0) {
          checkTime = new Date(checkDate)
          checkTime.setHours(0, 0, 0, 0)
        }
      }

      // Fallback: return the original time plus one hour
      const fallback = new Date(currentTime)
      fallback.setHours(fallback.getHours() + 1)
      return fallback
    },

    // New validation methods for smoother drag behavior
    isValidJobTimeRange(startDateTime, endDateTime) {
      if (this.timelineViewMode === 'days') {
        return this.isValidJobDateRange(startDateTime, endDateTime)
      } else {
        return this.isValidJobHourRange(startDateTime, endDateTime)
      }
    },

    isValidJobHourRange(startDateTime, endDateTime) {
      const dayName = format(startDateTime, 'EEEE')
      const daySchedule = this.workingSchedule[dayName]

      if (!daySchedule || daySchedule.length === 0) {
        return false
      }

      const startTimeString = format(startDateTime, 'HH:mm')
      const endTimeString = format(endDateTime, 'HH:mm')

      // Check if the job time range overlaps with any working time block
      // This allows jobs to span across multiple blocks or be within any single block
      return daySchedule.some(timeBlock => {
        // Check if there's any overlap between job time and this working block
        const blockStart = timeBlock.start
        const blockEnd = timeBlock.end

        // Job overlaps with block if:
        // - Job starts before block ends AND job ends after block starts
        return startTimeString < blockEnd && endTimeString > blockStart
      })
    },

    isValidJobDateRange(startDateTime, endDateTime) {
      // For day mode, check that all days in the range are working days
      let currentDate = new Date(startDateTime)
      currentDate.setHours(0, 0, 0, 0)

      const endDate = new Date(endDateTime)
      endDate.setHours(0, 0, 0, 0)

      while (currentDate <= endDate) {
        const dayName = format(currentDate, 'EEEE')
        const daySchedule = this.workingSchedule[dayName]

        // If any day in the range is not a working day, return false
        if (!daySchedule || daySchedule.length === 0) {
          return false
        }

        // Move to next day
        currentDate.setDate(currentDate.getDate() + 1)
      }

      return true
    },

    // Check if a specific date is a working day
    isWorkingDay(dateTime) {
      const dayName = format(dateTime, 'EEEE')
      const daySchedule = this.workingSchedule[dayName]
      return daySchedule && daySchedule.length > 0
    },

    findNearestValidTimeSlot(targetStartDateTime, duration) {
      const dayName = format(targetStartDateTime, 'EEEE')
      const daySchedule = this.workingSchedule[dayName]

      if (!daySchedule || daySchedule.length === 0) {
        // If no working hours for this day, find the next working day
        return this.findNextWorkingTime(targetStartDateTime, duration)
      }

      const targetTimeMinutes = this.timeToMinutes(format(targetStartDateTime, 'HH:mm'))
      const jobDurationMinutes = duration / (1000 * 60)
      let bestFit = null
      let minDistance = Infinity

      // Try to find the closest valid time slot within the same day
      for (const timeBlock of daySchedule) {
        const blockStartMinutes = this.timeToMinutes(timeBlock.start)
        const blockEndMinutes = this.timeToMinutes(timeBlock.end)

        // Check if job can fit in this time block
        if (jobDurationMinutes <= (blockEndMinutes - blockStartMinutes)) {
          // Try different positions within this time block
          const maxStartMinutes = blockEndMinutes - jobDurationMinutes

          for (let startMinutes = blockStartMinutes; startMinutes <= maxStartMinutes; startMinutes += 15) {
            const distance = Math.abs(startMinutes - targetTimeMinutes)

            if (distance < minDistance) {
              minDistance = distance
              const hours = Math.floor(startMinutes / 60)
              const minutes = startMinutes % 60

              const validStartTime = new Date(targetStartDateTime)
              validStartTime.setHours(hours, minutes, 0, 0)

              bestFit = {
                start_date: validStartTime.toISOString(),
                end_date: new Date(validStartTime.getTime() + duration).toISOString()
              }
            }
          }
        }
      }

      return bestFit || this.findNextWorkingTime(targetStartDateTime, duration)
    },

    // Working hours duration calculation methods
    calculateWorkingHoursDuration(startDateTime, endDateTime) {
      let totalMinutes = 0
      let currentDateTime = new Date(startDateTime)

      // Iterate through each day from start to end
      while (currentDateTime < endDateTime) {
        const dayName = format(currentDateTime, 'EEEE')
        const daySchedule = this.workingSchedule[dayName]

        if (daySchedule && daySchedule.length > 0) {
          // Get the start and end of the current day
          const dayStart = new Date(currentDateTime)
          dayStart.setHours(0, 0, 0, 0)
          const dayEnd = new Date(currentDateTime)
          dayEnd.setHours(23, 59, 59, 999)

          // Calculate the overlap between job time and this day
          const jobStartOnDay = currentDateTime > dayStart ? currentDateTime : dayStart
          const jobEndOnDay = endDateTime < dayEnd ? endDateTime : dayEnd

          if (jobStartOnDay < jobEndOnDay) {
            // Calculate working minutes for this day
            for (const timeBlock of daySchedule) {
              const [startHour, startMinute] = timeBlock.start.split(':').map(Number)
              const [endHour, endMinute] = timeBlock.end.split(':').map(Number)

              const blockStart = new Date(currentDateTime)
              blockStart.setHours(startHour, startMinute, 0, 0)
              const blockEnd = new Date(currentDateTime)
              blockEnd.setHours(endHour, endMinute, 0, 0)

              // Find overlap between job time and this working block
              const overlapStart = jobStartOnDay > blockStart ? jobStartOnDay : blockStart
              const overlapEnd = jobEndOnDay < blockEnd ? jobEndOnDay : blockEnd

              if (overlapStart < overlapEnd) {
                const overlapMinutes = (overlapEnd - overlapStart) / (1000 * 60)
                totalMinutes += overlapMinutes
              }
            }
          }
        }

        // Move to next day
        currentDateTime.setDate(currentDateTime.getDate() + 1)
        currentDateTime.setHours(0, 0, 0, 0)
      }

      return totalMinutes
    },

    addWorkingHoursDuration(startDateTime, durationMinutes) {
      let remainingMinutes = durationMinutes
      let currentDateTime = new Date(startDateTime)

      // Find the next working time if start is not in working hours
      if (!this.isWorkingTime(currentDateTime)) {
        currentDateTime = this.findNextWorkingTimeSlot(currentDateTime)
      }

      const maxDaysToCheck = 30 // Prevent infinite loops
      let daysChecked = 0

      while (remainingMinutes > 0 && daysChecked < maxDaysToCheck) {
        const dayName = format(currentDateTime, 'EEEE')
        const daySchedule = this.workingSchedule[dayName]

        if (daySchedule && daySchedule.length > 0) {
          // Process all working blocks for this day
          const currentTimeString = format(currentDateTime, 'HH:mm')
          let dayProcessed = false

          for (const timeBlock of daySchedule) {
            // Skip blocks that are before our current time
            if (currentTimeString > timeBlock.end) {
              continue
            }

            // Determine start time for this block
            let blockStartTime
            if (currentTimeString <= timeBlock.start) {
              // We're before this block, start from block start
              const [startHour, startMinute] = timeBlock.start.split(':').map(Number)
              blockStartTime = new Date(currentDateTime)
              blockStartTime.setHours(startHour, startMinute, 0, 0)
            } else if (currentTimeString < timeBlock.end) {
              // We're in the middle of this block, start from current time
              blockStartTime = new Date(currentDateTime)
            } else {
              // We're past this block, skip it
              continue
            }

            // Calculate block end time
            const [endHour, endMinute] = timeBlock.end.split(':').map(Number)
            const blockEndTime = new Date(currentDateTime)
            blockEndTime.setHours(endHour, endMinute, 0, 0)

            // Calculate available minutes in this block
            const availableMinutes = (blockEndTime - blockStartTime) / (1000 * 60)

            if (availableMinutes > 0) {
              if (remainingMinutes <= availableMinutes) {
                // Job ends within this block
                return new Date(blockStartTime.getTime() + (remainingMinutes * 60 * 1000))
              } else {
                // Use all available minutes in this block
                remainingMinutes -= availableMinutes
                dayProcessed = true
              }
            }
          }

          // If we processed some time today but still have remaining minutes, move to next day
          if (dayProcessed && remainingMinutes > 0) {
            // Move to next day
            currentDateTime.setDate(currentDateTime.getDate() + 1)
            currentDateTime.setHours(0, 0, 0, 0)
            daysChecked++
          } else if (!dayProcessed) {
            // No suitable blocks found today, move to next day
            currentDateTime.setDate(currentDateTime.getDate() + 1)
            currentDateTime.setHours(0, 0, 0, 0)
            daysChecked++
          }
        } else {
          // No working hours for this day (weekend), move to next day
          currentDateTime.setDate(currentDateTime.getDate() + 1)
          currentDateTime.setHours(0, 0, 0, 0)
          daysChecked++
        }
      }

      // Fallback: if we couldn't find enough working time, return what we have
      return currentDateTime
    },

    findNextWorkingTimeSlot(dateTime) {
      let checkDateTime = new Date(dateTime)
      const maxDaysToCheck = 7

      for (let dayOffset = 0; dayOffset < maxDaysToCheck; dayOffset++) {
        const dayName = format(checkDateTime, 'EEEE')
        const daySchedule = this.workingSchedule[dayName]

        if (daySchedule && daySchedule.length > 0) {
          const currentTimeString = format(checkDateTime, 'HH:mm')

          for (const timeBlock of daySchedule) {
            if (dayOffset === 0) {
              // For the current day, respect the current time
              if (currentTimeString <= timeBlock.start) {
                // Start of this working block
                const [startHour, startMinute] = timeBlock.start.split(':').map(Number)
                const workingStart = new Date(checkDateTime)
                workingStart.setHours(startHour, startMinute, 0, 0)
                return workingStart
              } else if (currentTimeString < timeBlock.end) {
                // We're already in a working block
                return checkDateTime
              }
            } else {
              // For future days, always start from the first working block
              const [startHour, startMinute] = timeBlock.start.split(':').map(Number)
              const workingStart = new Date(checkDateTime)
              workingStart.setHours(startHour, startMinute, 0, 0)
              return workingStart
            }
          }
        }

        // Move to next day and start from beginning
        checkDateTime.setDate(checkDateTime.getDate() + 1)
        checkDateTime.setHours(0, 0, 0, 0)
      }

      // Fallback: return original time
      return dateTime
    },

    // Focus job group methods
    toggleFocusJobGroup(job) {
      // Determine the group ID
      // If job is a subtask, use parent_job_id, otherwise use job_id
      const groupId = job.parent_job_id || job.job_id

      // Toggle: if already focused on this group, unfocus
      if (this.focusedJobGroup === groupId) {
        this[JobMutations.SET_FOCUSED_JOB_GROUP](null)
      } else {
        this[JobMutations.SET_FOCUSED_JOB_GROUP](groupId)
      }
    },

    isJobInFocusedGroup(job) {
      if (!this.focusedJobGroup) return true // No focus active, all visible

      // Check if this job is the parent or a subtask of the focused group
      const jobGroupId = job.parent_job_id || job.job_id
      return jobGroupId === this.focusedJobGroup
    },

    // Action menu state tracking
    handleActionMenuToggle(jobId, isOpen) {
      this.openActionMenuJobId = isOpen ? jobId : null
    },

    isJobExpanded(jobId) {
      return this.hoveredJobId === jobId || this.openActionMenuJobId === jobId
    },

    // Utility methods
    timeToMinutes(timeString) {
      const [hours, minutes] = timeString.split(':').map(Number)
      return hours * 60 + minutes
    },

    startTimeUpdater() {
      this.timeUpdateInterval = setInterval(() => {
        this.currentTime = new Date()
      }, 60000) // Update every minute
    },

    setupGlobalEventListeners() {
      document.addEventListener('mousemove', this.handleMouseMove)
      document.addEventListener('mouseup', this.handleMouseUp)
    },

    removeGlobalEventListeners() {
      document.removeEventListener('mousemove', this.handleMouseMove)
      document.removeEventListener('mouseup', this.handleMouseUp)
    }
  }
}
</script>

<style lang="scss" scoped>
.job-timeline-view {
  height: 100%;
  display: flex;
  flex-direction: column;

  .timeline-container {
    flex: 1;
    position: relative;

      .timeline-header {
        display: flex;
        position: sticky;
        top: 64px;
        background: white;
        border-bottom: 2px solid #e0e0e0;
        z-index: 5;

        @media (max-width: 959px) {
          top: 56px;
        }

        .job-labels-header {
          width: 200px;
          min-width: 200px;
          border-right: 2px solid #e0e0e0;
          display: flex;
          justify-content: center;
          align-items: center;
          font-size: 16px;
        }

        .time-header {
          flex: 1;
          display: flex;
          flex-direction: column;
          position: relative;

          // Action buttons container
          .header-actions {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 100%;
            pointer-events: none;
            z-index: 20;

            .actions-left {
              background: white;
              position: absolute;
              left: 8px;
              top: 8px;
              display: flex;
              gap: 4px;
              pointer-events: auto;
            }

            .actions-right {
              background: white;
              position: absolute;
              right: 8px;
              top: 8px;
              display: flex;
              gap: 4px;
              pointer-events: auto;
            }
          }

        .day-header-row {
          display: flex;
          border-bottom: 1px solid #e0e0e0;

          .day-header-cell {
            padding: 8px 4px;
            border-right: 1px solid #e0e0e0;
            text-align: center;
            font-size: 12px;
            font-weight: 600;
            color: #333;
            overflow: hidden;
            text-wrap: nowrap;
            text-overflow: ellipsis;

            &.non-working-day {
              background: #f5f5f5;
              color: #999;
            }
          }
        }

        .hour-header-row {
          display: flex;

          .hour-header-cell {
            padding: 8px 4px;
            border-right: 1px solid #e0e0e0;
            text-align: center;
            background: white;
            font-size: 11px;
            font-weight: 500;
            overflow: hidden;
            text-wrap: nowrap;
            text-overflow: ellipsis;

            &.current-time {
              background: #e3f2fd;
              border-left: 2px solid #2196f3;
              border-right: 2px solid #2196f3;
              font-weight: 600;
              color: #1976d2;
            }

            &.non-working {
              background: #f5f5f5;
              color: #999;
            }
          }
        }
      }
    }

    .timeline-body {
      position: relative;

      .pool-container {
        position: relative;
        background-color: color-mix(in srgb, var(--pool-color, #2196f3) 5%, transparent);

        .pool-title {
          font-size: 14px;
          font-weight: 700;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 190px;
          max-height: 40px;
        }

        &.pool-drag-target {
          &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            border: 3px dashed var(--pool-color, rgba(33, 150, 243, 0.6));
            background-color: color-mix(in srgb, var(--pool-color, #2196f3) 5%, transparent);
            pointer-events: none;
            z-index: 10;
            border-radius: 4px;
          }
        }
      }

      .lane-row {
        display: flex;

        &:last-child {
          border-bottom: none;
        }

        &.lane-dragging {
          opacity: 0.5;
          transform: scale(0.98);
        }

        &.lane-drag-over {
          border-top: 3px solid #2196f3;
          background-color: rgba(33, 150, 243, 0.1);
        }

        .lane-label {
          width: 200px;
          min-width: 200px;
          padding: 12px 12px 12px calc(12px + 5mm);
          border-right: 2px solid #e0e0e0;
          display: flex;
          align-items: center;

          .lane-info {

            .lane-title {
              max-width: 200px;
              max-height: 90px;
              font-size: 16px;
              font-weight: 400;
              display: flex;
              align-items: center;
              overflow: hidden;
              text-overflow: ellipsis;

              .drag-handle {
                cursor: grab;

                &:active {
                  cursor: grabbing;
                }
              }
            }
          }
        }

        .lane-timeline {
          flex: 1;
          position: relative;
          overflow: hidden;
          isolation: isolate; // Create stacking context to prevent cards from overlapping other lanes

            .time-grid {
              display: flex;
              position: absolute;
              top: 0;
              left: 0;
              bottom: 0;

              .time-grid-column {
                border-right: 1px solid #e0e0e0;

                &.current-time {
                  background: rgba(33, 150, 243, 0.05);
                }

                &.non-working {
                  background: rgba(0, 0, 0, 0.02);
                }

                &.weekend-divider {
                  border-right: 4px double #999;
                }

                &.day-divider {
                  border-right: 2px solid #999;
                }

                &.time-gap-divider {
                  border-right: 2px dashed #999;
                }
              }
            }

          .job-occupied {
            background-color: #78828c !important;

            &.subtask-card {
              border: #78828c 3px solid !important;
            }
          }

          .job-waiting {
            background-color: #b0b5bf !important;

            &.subtask-card {
              border:#b0b5bf 3px solid !important;
            }
          }

          .job-canceled {
            background-color: #ab8cf1 !important;

            &.subtask-card {
              border: #ab8cf1 3px solid !important;
            }
          }

          .job-doing {
            background-color: #f5ba54 !important;

            &.subtask-card {
              border: #f5ba54 3px solid !important;
            }
          }

          .job-done {
            background-color: #9bd5b7 !important;

            &.subtask-card {
              border: #9bd5b7 3px solid !important;
            }
          }

          .job-start-date-late {
            background-color: #f99d6d !important;

            &.subtask-card {
              border: #f99d6d 3px solid !important;
            }
          }

          .job-end-date-late {
            background-color: #ef898a !important;

            &.subtask-card {
              border: #ef898a 3px solid !important;
            }
          }

          .job-deadline-late {
            background-color: #c68182 !important;

            &.subtask-card {
              border: #c68182 3px solid !important;
            }
          }

          .job-card {
            position: absolute;
            border-radius: 12px;
            cursor: pointer;
            display: flex;
            align-items: center;
            padding: 0 8px;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            transition: all 0.2s ease;
            font-weight: 500;
            user-select: none;
            z-index: 3;
            overflow: hidden;
            will-change: transform; // Optimize rendering and create own layer

            &.subtask-card {
              background-color: white !important;
            }

            // Focus state styling
            &.job-focused {
              box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.5), 0 4px 8px rgba(0, 0, 0, 0.2);
              z-index: 999 !important;
              transform: scale(1.02);
            }

            &.job-dimmed {
              opacity: 0.3;
              filter: grayscale(50%);
              z-index: 1;
            }

            // Expanded state - apply when hovering or action menu is open
            &.job-expanded:not(.dragging):not(.resizing) {
              transform: translateY(-1px);
              box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
              z-index: 998 !important; // High z-index to appear above other cards in the same lane
              width: -moz-fit-content !important;
              min-width: fit-content;
              height: -moz-fit-content !important;
              min-height: fit-content;
              white-space: nowrap;
              position: relative;

              .job-content {
                .job-title {
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                }

                .job-info {
                  overflow: hidden;
                  text-overflow: ellipsis;
                }
              }
            }

            &.dragging {
              z-index: 1000 !important;
              transform: rotate(2deg);
              box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
              transition: none;
              pointer-events: none;

              // Keep the card visually expanded vertically while dragging
              width: -moz-fit-content !important;
              min-width: fit-content;
              height: -moz-fit-content !important;
              min-height: fit-content;
              white-space: nowrap;
              position: relative;
            }

            &.resizing {
              z-index: 997 !important;
              transition: none;
            }

            .resize-handle {
              position: absolute;
              top: 0;
              bottom: 0;
              width: 8px;
              cursor: ew-resize;
              //background: rgba(255, 255, 255, 0.2);
              mix-blend-mode: multiply;
              opacity: 0;
              transition: opacity 0.2s ease;

              &:hover {
                background: rgba(255, 255, 255, 0.4);
              }

              &.resize-left {
                left: 0;
                border-radius: 4px 0 0 4px;
              }

              &.resize-right {
                right: 0;
                border-radius: 0 4px 4px 0;
              }
            }

            &:hover .resize-handle {
              opacity: 1;
            }

            // Also show resize handles during active drag
            &.dragging .resize-handle {
              opacity: 1;
            }

            .job-content {
              flex: 1;
              pointer-events: none;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;

              .job-title {
                font-size: 14px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                line-height: 1;
                margin-bottom: 2px;
              }

              .job-info {
                font-size: 11px;
                line-height: 1;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
            }

            .job-actions {
              top: 0;
              transition: opacity 0.2s ease;
              display: flex;
              align-self: flex-start;
              display: none;
            }

            &:hover .job-actions {
              display: block;
            }

            // Also show actions during active drag
            &.dragging .job-actions {
              display: block;
            }
          }
        }
      }

      .add-lane-row {
        display: flex;
        border-bottom: 1px solid #e0e0e0;

        .lane-label {
          width: 200px;
          min-width: 200px;
          padding: 12px;
          border-right: 2px solid #e0e0e0;
          background: #fafafa;
          display: flex;
          align-items: center;
        }

        .lane-timeline {
          flex: 1;
          background: #f9f9f9;
        }
      }
    }

    .current-time-indicator {
      position: absolute;
      top: 0;
      bottom: 0;
      width: 2px;
      background-color: #ea4335;
      z-index: 4;
      pointer-events: none;

      .time-line {
        width: 100%;
        height: 100%;
        background-color: inherit;
      }

      .time-marker {
        position: absolute;
        top: -6px;
        left: -5px;
        width: 12px;
        height: 12px;
        background-color: #ea4335;
        border-radius: 50%;
        border: 2px solid white;
      }
    }

    .loading-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(255, 255, 255, 0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;

      .loading-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
        padding: 24px;
        background: white;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

        .loading-text {
          font-size: 14px;
          color: #666;
          font-weight: 500;
        }
      }
    }
  }
}

// Move confirmation dialog styling
.move-details {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;

  .detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #e9ecef;

    &:last-child {
      border-bottom: none;
    }

    .label {
      font-weight: 600;
      color: #495057;
      min-width: 60px;
    }

    .value {
      color: #212529;
      text-align: right;
      flex: 1;
      margin-left: 16px;
    }
  }
}

// Overlap confirmation dialog styling
.overlap-details {
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;

  .detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #ffeaa7;

    &:last-child {
      border-bottom: none;
    }

    .label {
      font-weight: 600;
      color: #856404;
      min-width: 80px;
    }

    .value {
      color: #856404;
      text-align: right;
      flex: 1;
      margin-left: 16px;
    }
  }
}

.overlapping-jobs {
  h4 {
    color: #721c24;
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 12px;
  }

  .overlapping-job-item {
    background: #f8d7da;
    border: 1px solid #f5c6cb;
    border-radius: 6px;
    padding: 12px;
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }

    .job-info {
      .job-name {
        font-weight: 600;
        color: #721c24;
        font-size: 14px;
        margin-bottom: 4px;
      }

      .job-time {
        color: #856404;
        font-size: 12px;
        font-weight: 500;
      }
    }
  }
}

// Responsive adjustments
@media (max-width: 960px) {
  .job-timeline-view {
    .timeline-container {
      .timeline-header {
        .job-labels-header {
          width: 150px;
          min-width: 150px;
        }

        .time-header .time-column-header {
          width: 50px;
          min-width: 50px;

          .date-label {
            font-size: 9px;
          }

          .time-label {
            font-size: 10px;
          }
        }
      }

      .timeline-body {
        .job-row .job-label {
          width: 150px;
          min-width: 150px;

          .job-info {
            .job-title {
              font-size: 12px;
            }

            .job-subtitle {
              font-size: 10px;
            }
          }
        }

        .job-row .job-timeline {
          .time-grid .time-grid-column {
            width: 50px;
            min-width: 50px;
          }

          .job-card {
            height: 40px;

            .task-content {
              .task-title {
                font-size: 11px;
              }

              .task-time {
                font-size: 9px;
              }
            }
          }
        }

        .add-job-row .job-label {
          width: 150px;
          min-width: 150px;
        }
      }
    }
  }
}
</style>
